import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMediaQuery, useTheme } from '@mui/material';
import SOSIcon from '../components/SOSIcon';
import PodcastIcon from '../components/PodcastIcon';
import LearningIcon from '../components/LearningIcon';
import DigitaleSprechstundeIcon from '../components/DigitaleSprechstundeIcon';
import InviteIcon from '../components/InviteIcon';
import ReportIcon from '../components/ReportIcon';
import EventSuggestionIcon from '../components/EventIcon';
import CommunityQuestionIcon from '../components/CommunityQuestionIcon';
import NotificationBell from '../components/NotificationBell';
import Sidebar, { drawerWidth, collapsedDrawerWidth } from '../components/Sidebar';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Container,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
  Fade,
  Slide,
  Collapse,
  CardActions,
  Button,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Map as MapIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Groups as GroupsIcon,
  Description as DescriptionIcon,
  Link as LinkIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Language as LanguageIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  ChevronRight as ChevronRightIcon,
  Forum as ForumIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
  LocationOn as LocationOnIcon,
  Public as PublicIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { API_CONFIG } from '../config/config';
import logo from '../assets/logo_trans_icon.png';
import { MapContainer, TileLayer, Marker, Popup, Polygon } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import 'echarts-wordcloud';


// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Enhanced styled components with blue color scheme
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%)',
  borderBottom: '3px solid #2196f3',
  boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
  backdropFilter: 'blur(10px)',
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  maxWidth: 1400,
  padding: theme.spacing(4),
  width: '100%',
  overflowX: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    maxWidth: '100%',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

const StatusBar = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
  borderLeft: '6px solid #2196f3',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: 12,
  color: '#ffffff',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
}));

const DashboardCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 16,
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(3),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#42a5f5',
  marginBottom: theme.spacing(3),
  borderBottom: '2px solid rgba(66, 165, 245, 0.3)',
  paddingBottom: theme.spacing(2),
  fontWeight: 600,
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: 0,
    width: '60px',
    height: '2px',
    background: 'linear-gradient(90deg, #2196f3, #42a5f5)',
  },
}));

const StyledFooter = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%)',
  color: '#aaa',
  textAlign: 'center',
  padding: theme.spacing(4),
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  marginTop: theme.spacing(6),
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: 20,
  fontWeight: 500,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.8) 0%, rgba(15, 15, 15, 0.8) 100%)',
  borderRadius: 12,
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateX(4px)',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(66, 165, 245, 0.3)',
  },
}));


const PodcastBanner = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '80px', // Positioned after the app bar
  right: '-150px', // Moved more to the left (less negative)
  width: '350px', // Slightly smaller width
  height: '50px', // Slightly smaller height
  background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff6b35 100%)',
  transform: 'rotate(15deg)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 8px 32px rgba(255, 107, 53, 0.4)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'rotate(15deg) scale(1.05)',
    boxShadow: '0 12px 40px rgba(255, 107, 53, 0.6)',
    right: '-130px', // Adjusted hover position
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
    animation: 'shimmer 3s infinite',
  },
  '@keyframes shimmer': {
    '0%': {
      transform: 'translateX(-100%)',
    },
    '100%': {
      transform: 'translateX(100%)',
    },
  },
}));

const PodcastPopup = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '400px',
  maxWidth: '90vw',
  background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
  border: '2px solid #ff6b35',
  borderRadius: '16px',
  padding: theme.spacing(3),
  zIndex: 10000,
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8), 0 0 0 1000px rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(10px)',
  animation: 'popupSlideIn 0.5s ease-out',
  '@keyframes popupSlideIn': {
    '0%': {
      opacity: 0,
      transform: 'translate(-50%, -50%) scale(0.8)',
    },
    '100%': {
      opacity: 1,
      transform: 'translate(-50%, -50%) scale(1)',
    },
  },
}));

const PodcastPopupOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)',
  zIndex: 9999,
  backdropFilter: 'blur(5px)',
}));

// Community Q&A Ticker Styled Components
const QATickerContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(66, 165, 245, 0.1) 100%)',
  borderBottom: '2px solid rgba(66, 165, 245, 0.3)',
  borderTop: '2px solid rgba(66, 165, 245, 0.2)',
  padding: theme.spacing(1.5, 2),
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1000,
  backdropFilter: 'blur(10px)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1, 1.5),
  },
}));

const QATickerContent = styled(Box)(({ theme, isPaused }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
  width: 'max-content',
  animation: 'scroll 90s linear infinite',
  animationPlayState: isPaused ? 'paused' : 'running',
  animationFillMode: 'both',
  willChange: 'transform',
  '&:hover': {
    animationPlayState: 'paused',
  },
  '@keyframes scroll': {
    '0%': {
      transform: 'translateX(0)',
    },
    '100%': {
      transform: 'translateX(-50%)',
    },
  },
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(2),
    animation: 'scroll 70s linear infinite',
    animationPlayState: isPaused ? 'paused' : 'running',
    animationFillMode: 'both',
  },
}));

const QAItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '8px',
  transition: 'all 0.2s ease-in-out',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  touchAction: 'manipulation',
  '&:hover': {
    background: 'rgba(66, 165, 245, 0.15)',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(66, 165, 245, 0.2)',
  },
  '&:active': {
    transform: 'translateY(0)',
    background: 'rgba(66, 165, 245, 0.25)',
  },
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
  },
}));


// Function to get the calendar week number (ISO 8601)
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Function to get event type label
const getEventTypeLabel = (eventType) => {
  const eventTypes = {
    'cybersecurity': 'Cybersecurity',
    'conference': 'Konferenz',
    'training': 'Schulung',
    'webinar': 'Webinar',
    'digitale_sprechstunde': 'Digitale Sprechstunde',
    'podcast': 'Podcast',
    'videocast': 'Videocast',
    'sonstige': 'Sonstige'
  };
  return eventTypes[eventType] || 'Cybersecurity';
};

// Function to get target audience label
const getTargetAudienceLabel = (targetAudience) => {
  const targetAudiences = {
    'kinder_jugendliche': 'Kinder & Jugendliche',
    'bevoelkerung': 'Bevölkerung',
    'unternehmen': 'Unternehmen',
    'behoerden': 'Behörden',
    'militaer': 'Militär',
    'keine_angabe': 'Keine Angabe'
  };
  return targetAudiences[targetAudience] || 'Bevölkerung';
};

// Function to get country flag emoji
const getCountryFlag = (country) => {
  if (!country) {
    return '🌍'; // World citizen flag
  }
  
  const countryFlags = {
    'Deutschland': '🇩🇪',
    'Germany': '🇩🇪',
    'Österreich': '🇦🇹',
    'Austria': '🇦🇹',
    'Schweiz': '🇨🇭',
    'Switzerland': '🇨🇭',
    'Frankreich': '🇫🇷',
    'France': '🇫🇷',
    'Italien': '🇮🇹',
    'Italy': '🇮🇹',
    'Spanien': '🇪🇸',
    'Spain': '🇪🇸',
    'Niederlande': '🇳🇱',
    'Netherlands': '🇳🇱',
    'Belgien': '🇧🇪',
    'Belgium': '🇧🇪',
    'Polen': '🇵🇱',
    'Poland': '🇵🇱',
    'Tschechien': '🇨🇿',
    'Czech Republic': '🇨🇿',
    'Dänemark': '🇩🇰',
    'Denmark': '🇩🇰',
    'Schweden': '🇸🇪',
    'Sweden': '🇸🇪',
    'Norwegen': '🇳🇴',
    'Norway': '🇳🇴',
    'Finnland': '🇫🇮',
    'Finland': '🇫🇮',
    'Vereinigtes Königreich': '🇬🇧',
    'United Kingdom': '🇬🇧',
    'UK': '🇬🇧',
    'Irland': '🇮🇪',
    'Ireland': '🇮🇪',
    'Portugal': '🇵🇹',
    'Griechenland': '🇬🇷',
    'Greece': '🇬🇷',
    'Luxemburg': '🇱🇺',
    'Luxembourg': '🇱🇺',
    'Ungarn': '🇭🇺',
    'Hungary': '🇭🇺',
    'Slowakei': '🇸🇰',
    'Slovakia': '🇸🇰',
    'Slowenien': '🇸🇮',
    'Slovenia': '🇸🇮',
    'Kroatien': '🇭🇷',
    'Croatia': '🇭🇷',
    'Rumänien': '🇷🇴',
    'Romania': '🇷🇴',
    'Bulgarien': '🇧🇬',
    'Bulgaria': '🇧🇬',
    'Estland': '🇪🇪',
    'Estonia': '🇪🇪',
    'Lettland': '🇱🇻',
    'Latvia': '🇱🇻',
    'Litauen': '🇱🇹',
    'Lithuania': '🇱🇹',
    'Vereinigte Staaten': '🇺🇸',
    'United States': '🇺🇸',
    'USA': '🇺🇸',
    'Kanada': '🇨🇦',
    'Canada': '🇨🇦',
    'Australien': '🇦🇺',
    'Australia': '🇦🇺',
    'Neuseeland': '🇳🇿',
    'New Zealand': '🇳🇿',
    'Japan': '🇯🇵',
    'China': '🇨🇳',
    'Indien': '🇮🇳',
    'India': '🇮🇳',
    'Brasilien': '🇧🇷',
    'Brazil': '🇧🇷',
    'Mexiko': '🇲🇽',
    'Mexico': '🇲🇽',
    'Argentinien': '🇦🇷',
    'Argentina': '🇦🇷',
    'Südafrika': '🇿🇦',
    'South Africa': '🇿🇦',
    'Südkorea': '🇰🇷',
    'South Korea': '🇰🇷',
    'Türkei': '🇹🇷',
    'Turkey': '🇹🇷',
    'Russland': '🇷🇺',
    'Russia': '🇷🇺',
    'Israel': '🇮🇱',
    'Ägypten': '🇪🇬',
    'Egypt': '🇪🇬',
  };
  
  // Try exact match first
  if (countryFlags[country]) {
    return countryFlags[country];
  }
  
  // Try case-insensitive match
  const countryLower = country.toLowerCase();
  const found = Object.keys(countryFlags).find(key => key.toLowerCase() === countryLower);
  if (found) {
    return countryFlags[found];
  }
  
  // Return world flag if country not found
  return '🌍';
};

const Bevoelkerung = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [dailySecurityTip, setDailySecurityTip] = useState(null);
  const [ninaWarnings, setNinaWarnings] = useState([]);
  const [fakeShops, setFakeShops] = useState([]);
  const [googleTrends, setGoogleTrends] = useState([]);
  const [mimikamaFaktenchecks, setMimikamaFaktenchecks] = useState([]);
  const [buergercertEntries, setBuergercertEntries] = useState([]);
  const [justYouPlatforms, setJustYouPlatforms] = useState([]);
  const [justYouSummary, setJustYouSummary] = useState({});
  const [cyberWeather, setCyberWeather] = useState(null);
  const [phishingWarnings, setPhishingWarnings] = useState(null);
  const [buzzlatorTerms, setBuzzlatorTerms] = useState([]);
  const [buzzlatorTermsLoading, setBuzzlatorTermsLoading] = useState(false);
  const [communityReports, setCommunityReports] = useState([]);
  const [currentWeekEvents, setCurrentWeekEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [communityQA, setCommunityQA] = useState([]);
  const [tickerPaused, setTickerPaused] = useState(false);
  const [selectedQA, setSelectedQA] = useState(null);
  const [qaDialogOpen, setQaDialogOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Handle Q&A item click
  const handleQAClick = (qa, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedQA(qa);
    setQaDialogOpen(true);
    setTickerPaused(true); // Pause ticker when dialog opens
  };

  const handleCloseQADialog = () => {
    setQaDialogOpen(false);
    setSelectedQA(null);
    // Resume ticker when dialog closes
    setTickerPaused(false);
  };

  // Toggle card expansion
  const toggleCardExpansion = (eventId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const fetchData = async () => {
    setLoading(true);
    
    // Fetch daily security tip from API
    try {
      const securityTipResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/daily-security-tip`);
      setDailySecurityTip(securityTipResponse.data);
    } catch (securityTipError) {
      console.warn('Failed to fetch daily security tip:', securityTipError);
      setDailySecurityTip(null);
    }
    
    // Fetch NINA warnings from API
    try {
      const warningsResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/nina-warnings`);
      setNinaWarnings(warningsResponse.data.warnings || []);
    } catch (warningsError) {
      console.warn('Failed to fetch NINA warnings:', warningsError);
      setNinaWarnings([]);
    }
    
    // Fetch fake shops from API
    try {
      const fakeShopsResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/fake-shops`);
      setFakeShops(fakeShopsResponse.data.fake_shops || []);
    } catch (fakeShopsError) {
      console.warn('Failed to fetch fake shops:', fakeShopsError);
      setFakeShops([]);
    }
    
    // Fetch Google Trends from API
    try {
      const googleTrendsResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/google-trends`);
      setGoogleTrends(googleTrendsResponse.data.trending_topics || []);
    } catch (googleTrendsError) {
      console.warn('Failed to fetch Google Trends:', googleTrendsError);
      setGoogleTrends([]);
    }
    
    // Fetch Mimikama Faktenchecks from API
    try {
      const mimikamaResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/mimikama-faktenchecks`);
      setMimikamaFaktenchecks(mimikamaResponse.data.faktenchecks || []);
    } catch (mimikamaError) {
      console.warn('Failed to fetch Mimikama Faktenchecks:', mimikamaError);
      setMimikamaFaktenchecks([]);
    }
    
    // Fetch Buergercert Entries from API
    try {
      const buergercertResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/buergercert-entries`);
      setBuergercertEntries(buergercertResponse.data.entries || []);
    } catch (buergercertError) {
      console.warn('Failed to fetch Buergercert entries:', buergercertError);
      setBuergercertEntries([]);
    }
    
    // Fetch Just You Platform Status from API
    try {
      const justYouResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/just-you-platform-status`);
      setJustYouPlatforms(justYouResponse.data.platforms || []);
      setJustYouSummary(justYouResponse.data.summary || {});
    } catch (justYouError) {
      console.warn('Failed to fetch Just You platform status:', justYouError);
      setJustYouPlatforms([]);
      setJustYouSummary({});
    }
    
    // Fetch Cyber Weather from API
    try {
      const cyberWeatherResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/cyber-weather`);
      setCyberWeather(cyberWeatherResponse.data.cyber_weather);
    } catch (cyberWeatherError) {
      console.warn('Failed to fetch Cyber Weather:', cyberWeatherError);
      setCyberWeather(null);
    }
      
      // Fetch phishing warnings from the last 24h
      try {
        const phishingWarningsResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/phishing-warnings`);
        setPhishingWarnings(phishingWarningsResponse.data);
      } catch (warningsError) {
        console.warn('Failed to fetch phishing warnings:', warningsError);
        setPhishingWarnings(null);
      }
      
    // Fetch buzzlator terms from API
    try {
      console.log('Fetching buzzlator terms from:', `${API_CONFIG.BASE_URL}/elasticsearch/buzzlator-terms`);
      const buzzlatorTermsResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/buzzlator-terms`);
      console.log('Buzzlator terms response:', buzzlatorTermsResponse.data);
      console.log('Buzzlator terms array:', buzzlatorTermsResponse.data.buzzlator_terms);
      console.log('Buzzlator terms length:', buzzlatorTermsResponse.data.buzzlator_terms?.length);
      setBuzzlatorTerms(buzzlatorTermsResponse.data.buzzlator_terms || []);
      console.log('Set buzzlator terms state to:', buzzlatorTermsResponse.data.buzzlator_terms || []);
    } catch (buzzlatorError) {
      console.warn('Failed to fetch buzzlator terms:', buzzlatorError);
      setBuzzlatorTerms([]);
    }
    
    // Fetch community reports from API
    try {
      const communityReportsResponse = await axios.get(`${API_CONFIG.BASE_URL}/reports/reviewed?limit=10`);
      setCommunityReports(communityReportsResponse.data.reports || []);
    } catch (communityReportsError) {
      console.warn('Failed to fetch community reports:', communityReportsError);
      setCommunityReports([]);
    }
    
    // Fetch current week calendar events
    await fetchCurrentWeekEvents();
    
    // Fetch community Q&A for ticker
    try {
      // TODO: Update this endpoint when your API is ready
      // For now, using a placeholder that returns empty array
      const qaResponse = await axios.get(`${API_CONFIG.BASE_URL}/community/qa?limit=10`);
      setCommunityQA(qaResponse.data.qa || []);
    } catch (qaError) {
      console.warn('Failed to fetch community Q&A:', qaError);
      // Set some sample data for demonstration
      setCommunityQA([
        {
          id: 1,
          question: "Wie erkenne ich eine Phishing-E-Mail?",
          answer: "Achte auf verdächtige Absender, Rechtschreibfehler und dringende Handlungsaufforderungen.",
          expert: "Cybersicherheitsexperte",
          kategorie: "Phishing",
          who_asked: "Max Mustermann",
          city: "Berlin",
          country: "Deutschland"
        },
        {
          id: 2,
          question: "Was ist ein sicheres Passwort?",
          answer: "Ein sicheres Passwort hat mindestens 12 Zeichen, enthält Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen.",
          expert: "IT-Sicherheitsspezialist",
          kategorie: "Passwörter",
          who_asked: "Anna Schmidt",
          city: "München",
          country: "Deutschland"
        }
      ]);
    }
    
    setError(null);
    setLastUpdated(new Date());
    setLoading(false);
  };

  // Function to fetch only buzzlator terms (used for real-time updates)
  const fetchBuzzlatorTerms = useCallback(async (expectedTerm = null, retryCount = 0) => {
    setBuzzlatorTermsLoading(true);
    try {
      console.log(`Refreshing buzzlator terms after new buzzword explained... (attempt ${retryCount + 1})`);
      const buzzlatorTermsResponse = await axios.get(`${API_CONFIG.BASE_URL}/elasticsearch/buzzlator-terms`);
      console.log('Updated buzzlator terms response:', buzzlatorTermsResponse.data);
      const newTerms = buzzlatorTermsResponse.data.buzzlator_terms || [];
      setBuzzlatorTerms(newTerms);
      console.log('Updated buzzlator terms state to:', newTerms);
      
      // If we're looking for a specific term and it's not found, retry
      if (expectedTerm && retryCount < 3) {
        const termExists = newTerms.some(term => term.term === expectedTerm);
        if (!termExists) {
          console.log(`Term "${expectedTerm}" not found yet, retrying in 1 second...`);
          setTimeout(() => {
            fetchBuzzlatorTerms(expectedTerm, retryCount + 1);
          }, 1000);
          return;
        } else {
          console.log(`Term "${expectedTerm}" found successfully!`);
        }
      }
    } catch (buzzlatorError) {
      console.warn('Failed to refresh buzzlator terms:', buzzlatorError);
      // Don't set empty array on error, keep existing data
    } finally {
      setBuzzlatorTermsLoading(false);
    }
  }, []);

  // Function to fetch current week's calendar events
  const fetchCurrentWeekEvents = useCallback(async () => {
    setCalendarLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found, skipping calendar events fetch');
        setCurrentWeekEvents([]);
        return;
      }

      // Calculate current week start and end dates (Monday to Sunday)
      const now = new Date();
      const startOfWeek = new Date(now);
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Calculate days to Monday
      startOfWeek.setDate(now.getDate() + daysToMonday);
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week (Sunday)
      endOfWeek.setHours(23, 59, 59, 999);

      const response = await axios.get(`${API_CONFIG.BASE_URL}/calendar/events`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        params: {
          start_date: startOfWeek.toISOString(),
          end_date: endOfWeek.toISOString(),
        },
      });

      setCurrentWeekEvents(response.data || []);
    } catch (error) {
      console.warn('Failed to fetch current week events:', error);
      setCurrentWeekEvents([]);
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 10 minutes
    const interval = setInterval(() => {
      fetchData();
    }, 10 * 60 * 1000);

    // Listen for calendar events updates
    const handleCalendarEventsUpdated = () => {
      console.log('Calendar events updated, refreshing current week events...');
      fetchCurrentWeekEvents();
    };
    
    window.addEventListener('calendarEventsUpdated', handleCalendarEventsUpdated);

    return () => {
      clearInterval(interval);
      window.removeEventListener('calendarEventsUpdated', handleCalendarEventsUpdated);
    };
  }, []);

  // Listen for buzzword explained events to refresh buzzlator terms
  useEffect(() => {
    const handleBuzzwordExplained = (event) => {
      console.log('Buzzword explained event received:', event.detail);
      const expectedTerm = event.detail.term;
      
      // Add a small delay to allow Elasticsearch to index the new document
      setTimeout(() => {
        console.log(`Refreshing buzzlator terms after delay, looking for term: "${expectedTerm}"...`);
        fetchBuzzlatorTerms(expectedTerm);
      }, 2000); // 2 second delay
    };

    window.addEventListener('buzzwordExplained', handleBuzzwordExplained);

    return () => {
      window.removeEventListener('buzzwordExplained', handleBuzzwordExplained);
    };
  }, [fetchBuzzlatorTerms]);





  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)',
      minHeight: '100vh', 
      color: '#ffffff',
      position: 'relative',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(66, 165, 245, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0,
      }
    }}>
      {/* Podcast Icon Component */}
      <PodcastIcon />
      
      {/* Learning Icon Component */}
      <LearningIcon />
      
      {/* Digitale Sprechstunde Icon Component */}
      <DigitaleSprechstundeIcon />
      
      {/* Invite Icon Component */}
      <InviteIcon />
      
      {/* Report Icon Component */}
      <ReportIcon />

      {/* SOS Icon Component */}
      <SOSIcon />
      
      {/* Community Question Icon Component */}
      <CommunityQuestionIcon />
      
      {/* Event Suggestion Icon Component */}
      <EventSuggestionIcon />
      
      {/* Sidebar */}
      <Sidebar 
        open={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        mobileOpen={mobileSidebarOpen}
        onMobileToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
      />
      
      <Box sx={{ 
        display: 'flex',
        ml: { sm: sidebarOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` },
        transition: (theme) => theme.transitions.create('margin-left', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}>
        <Box component="main" sx={{ flexGrow: 1, width: '100%' }}>
          <StyledAppBar position="static" sx={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        <Toolbar sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
          {/* Logo and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 2 }}>
            <Box
              component="img"
              src={logo}
              alt="CyberLagebild Logo"
              sx={{
                height: { xs: 60, sm: 80, md: 120 },
                width: 'auto',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="h5" 
                component="h1" 
                sx={{ 
                  color: 'white',
                  fontWeight: 600,
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                cyberlagebild.de - Sicherheit für alle
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  display: { xs: 'none', sm: 'block' },
                  mt: 0.5
                }}
              >
                Deutschland 🇩🇪 | Österreich 🇦🇹 | Schweiz 🇨🇭
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: 3, 
            alignItems: 'center' 
          }}>
            <Typography component="a" href="https://www.cyberlagebild.de" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                textDecoration: 'underline',
                color: '#e3f2fd',
                transform: 'scale(1.05)',
              } 
            }}>
              Cyberlagebild
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/news" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                textDecoration: 'underline',
                color: '#e3f2fd',
                transform: 'scale(1.05)',
              } 
            }}>
              News
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/ueber-uns" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                textDecoration: 'underline',
                color: '#e3f2fd',
                transform: 'scale(1.05)',
              } 
            }}>
              Über uns
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/mehrwerte" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                textDecoration: 'underline',
                color: '#e3f2fd',
                transform: 'scale(1.05)',
              } 
            }}>
              Mehrwerte
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/community" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                textDecoration: 'underline',
                color: '#e3f2fd',
                transform: 'scale(1.05)',
              } 
            }}>
              Community
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/faq" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                textDecoration: 'underline',
                color: '#e3f2fd',
                transform: 'scale(1.05)',
              } 
            }}>
              FAQ
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/contact-2" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                textDecoration: 'underline',
                color: '#e3f2fd',
                transform: 'scale(1.05)',
              } 
            }}>
              Kontakt
            </Typography>
          </Box>

          {/* User Actions */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, sm: 1 }, 
            ml: { xs: 1, sm: 2, md: 3 },
            flexWrap: 'nowrap'
          }}>
            <NotificationBell />
            <Tooltip title={user?.email || 'Benutzer'} arrow>
              <IconButton
                color="inherit"
                onClick={() => navigate('/profile')}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>

            {/* Admin Dashboard Link - Only show for admin users */}
            {user?.is_superuser && (
              <Tooltip title="Admin Dashboard" arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/admin')}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            )}

            <Button
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={logout}
              sx={{
                fontFamily: 'Futura Book, sans-serif',
                fontWeight: 400,
                color: 'white',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '6px 8px', sm: '8px 16px' },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              Logout
            </Button>

            {/* Sidebar Menu Button - Always visible on mobile (portrait and landscape) */}
            <IconButton
              color="inherit"
              onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              sx={{
                display: { xs: 'flex', md: 'none' }, // Show on all mobile sizes (xs and sm)
                color: 'white',
                ml: 1,
                minWidth: '48px',
                minHeight: '48px',
                zIndex: 1300, // Ensure it's above other elements
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:active': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                }
              }}
              aria-label="open sidebar"
            >
              <PersonIcon sx={{ fontSize: '28px' }} />
            </IconButton>

            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{
                display: { xs: 'flex', md: 'none' },
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>

        {/* Mobile Menu */}
        <Box sx={{
          display: { xs: mobileMenuOpen ? 'block' : 'none', md: 'none' },
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: 2,
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography component="a" href="https://www.cyberlagebild.de" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              padding: { xs: 1.5, sm: 1 },
              borderRadius: 1,
              minHeight: { xs: '44px', sm: 'auto' },
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#e3f2fd',
              } 
            }}>
              Cyberlagebild
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/news" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              padding: { xs: 1.5, sm: 1 },
              borderRadius: 1,
              minHeight: { xs: '44px', sm: 'auto' },
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#e3f2fd',
              } 
            }}>
              Einstieg
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/ueber-uns" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              padding: { xs: 1.5, sm: 1 },
              borderRadius: 1,
              minHeight: { xs: '44px', sm: 'auto' },
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#e3f2fd',
              } 
            }}>
              Über uns
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/mehrwerte" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              padding: { xs: 1.5, sm: 1 },
              borderRadius: 1,
              minHeight: { xs: '44px', sm: 'auto' },
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#e3f2fd',
              } 
            }}>
              Mehrwerte
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/community" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              padding: { xs: 1.5, sm: 1 },
              borderRadius: 1,
              minHeight: { xs: '44px', sm: 'auto' },
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#e3f2fd',
              } 
            }}>
              Community
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/faq" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              padding: { xs: 1.5, sm: 1 },
              borderRadius: 1,
              minHeight: { xs: '44px', sm: 'auto' },
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#e3f2fd',
              } 
            }}>
              FAQ
            </Typography>

            <Typography component="a" href="https://www.cyberlagebild.de/contact-2" target="_blank" rel="noopener noreferrer" sx={{ 
              color: 'white', 
              textDecoration: 'none', 
              fontWeight: 500,
              padding: { xs: 1.5, sm: 1 },
              borderRadius: 1,
              minHeight: { xs: '44px', sm: 'auto' },
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#e3f2fd',
              } 
            }}>
              Kontakt
            </Typography>
          </Box>
        </Box>
      </StyledAppBar>

      {/* Community Q&A Ticker */}
      {communityQA && communityQA.length > 0 && (
        <QATickerContainer
          onMouseEnter={() => !qaDialogOpen && setTickerPaused(true)}
          onMouseLeave={() => !qaDialogOpen && setTickerPaused(false)}
          sx={{
            display: { xs: 'none', sm: 'block' }, // Hide on mobile, show on tablet/desktop
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              minWidth: 'fit-content',
              flexShrink: 0
            }}>
              <ForumIcon sx={{ color: '#42a5f5', fontSize: 20 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#42a5f5', 
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Community Q&A
              </Typography>
              <Tooltip 
                title="Dieser Ticker zeigt aktuelle Fragen zur Cybersicherheit aus unserer Community. Klicke auf eine Frage, um Details und Antworten zu sehen."
                arrow
                placement="top"
              >
                <IconButton
                  size="small"
                  sx={{
                    color: '#42a5f5',
                    padding: '2px',
                    '&:hover': {
                      backgroundColor: 'rgba(66, 165, 245, 0.1)',
                    }
                  }}
                >
                  <HelpIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ 
              overflow: 'hidden', 
              width: '100%',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '40px',
                background: 'linear-gradient(to right, rgba(33, 150, 243, 0.15), transparent)',
                zIndex: 1,
                pointerEvents: 'none',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '40px',
                background: 'linear-gradient(to left, rgba(33, 150, 243, 0.15), transparent)',
                zIndex: 1,
                pointerEvents: 'none',
              },
            }}>
              <QATickerContent isPaused={tickerPaused}>
                {/* Duplicate content for seamless loop */}
                {[...communityQA, ...communityQA].map((qa, idx) => (
                  <QAItem 
                    key={`${qa.id}-${idx}`}
                    onClick={(e) => handleQAClick(qa, e)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Typography 
                      sx={{ 
                        fontSize: 20, 
                        lineHeight: 1,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '24px'
                      }}
                      title={qa.country || 'Weltbürger'}
                    >
                      {getCountryFlag(qa.country)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#ffffff', 
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        maxWidth: '500px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <strong style={{ color: '#42a5f5' }}>Q:</strong> {qa.question.length > 60 ? `${qa.question.substring(0, 60)}...` : qa.question}
                    </Typography>
                    <ChevronRightIcon sx={{ color: '#42a5f5', fontSize: 16, flexShrink: 0 }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)', 
                        fontSize: '0.875rem',
                        maxWidth: '400px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <strong style={{ color: '#66bb6a' }}>A:</strong> {qa.answer.length > 50 ? `${qa.answer.substring(0, 50)}...` : qa.answer}
                    </Typography>
                  </QAItem>
                ))}
              </QATickerContent>
            </Box>
          </Box>
        </QATickerContainer>
      )}

      {/* Mobile Q&A Ticker - Compact Version */}
      {communityQA && communityQA.length > 0 && (
        <QATickerContainer
          sx={{
            display: { xs: 'block', sm: 'none' }, // Show only on mobile
            padding: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
            <ForumIcon sx={{ color: '#42a5f5', fontSize: 18, flexShrink: 0 }} />
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#42a5f5', 
                fontWeight: 600,
                fontSize: '0.7rem',
                flexShrink: 0,
              }}
            >
              Q&A:
            </Typography>
            {communityQA.slice(0, 3).map((qa, idx) => (
              <Box 
                key={qa.id || idx} 
                onClick={(e) => handleQAClick(qa, e)}
                onMouseDown={(e) => e.preventDefault()}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5, 
                  flexShrink: 0,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    background: 'rgba(66, 165, 245, 0.15)',
                  },
                  '&:active': {
                    background: 'rgba(66, 165, 245, 0.25)',
                  },
                }}
              >
                <Typography 
                  sx={{ 
                    fontSize: 16, 
                    lineHeight: 1,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '20px'
                  }}
                  title={qa.country || 'Weltbürger'}
                >
                  {getCountryFlag(qa.country)}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#ffffff', 
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ color: '#42a5f5' }}>Q:</span> {qa.question.length > 30 ? `${qa.question.substring(0, 30)}...` : qa.question}
                </Typography>
                {idx < 2 && <ChevronRightIcon sx={{ color: '#42a5f5', fontSize: 14 }} />}
              </Box>
            ))}
          </Box>
        </QATickerContainer>
      )}

      {/* Q&A Dialog - Full Details */}
      <Dialog
        open={qaDialogOpen}
        onClose={handleCloseQADialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '16px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
            border: '2px solid rgba(66, 165, 245, 0.3)',
            maxHeight: isMobile ? '100vh' : '90vh',
            margin: isMobile ? 0 : '32px',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
            color: 'white',
            textAlign: 'center',
            py: isMobile ? 2 : 3,
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ForumIcon />
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                component="div" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: isMobile ? '1.1rem' : '1.5rem'
                }}
              >
                Community Q&A
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseQADialog}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 2 : 3, maxHeight: isMobile ? 'calc(100vh - 140px)' : '70vh', overflow: 'auto' }}>
          {selectedQA && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Question Metadata */}
              {(selectedQA.kategorie || selectedQA.who_asked || selectedQA.is_anonymous || selectedQA.city || selectedQA.country) && (
                <Box
                  sx={{
                    p: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2,
                  }}
                >
                  {selectedQA.kategorie && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CategoryIcon sx={{ color: '#42a5f5', fontSize: 18 }} />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                          }}
                        >
                          Kategorie
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ffffff',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {selectedQA.kategorie}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {(selectedQA.who_asked || selectedQA.is_anonymous) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ color: '#42a5f5', fontSize: 18 }} />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                          }}
                        >
                          Gefragt von
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ffffff',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {selectedQA.is_anonymous 
                            ? 'Anonym' 
                            : (selectedQA.who_asked 
                                ? selectedQA.who_asked.split(' ')[0] 
                                : 'Unbekannt')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {selectedQA.city && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon sx={{ color: '#42a5f5', fontSize: 18 }} />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                          }}
                        >
                          Stadt
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ffffff',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {selectedQA.city}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {selectedQA.country && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PublicIcon sx={{ color: '#42a5f5', fontSize: 18 }} />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                          }}
                        >
                          Land
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ffffff',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {selectedQA.country}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Question Section */}
              <Box
                sx={{
                  p: 3,
                  background: 'rgba(66, 165, 245, 0.1)',
                  border: '2px solid rgba(66, 165, 245, 0.3)',
                  borderRadius: 2,
                  borderLeft: '6px solid #42a5f5',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ChatIcon sx={{ color: '#42a5f5', fontSize: 24 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#42a5f5',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '1rem' : '1.25rem',
                    }}
                  >
                    Frage
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#ffffff',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    lineHeight: 1.6,
                  }}
                >
                  {selectedQA.question}
                </Typography>
              </Box>

              {/* Answer Section */}
              <Box
                sx={{
                  p: 3,
                  background: 'rgba(102, 187, 106, 0.1)',
                  border: '2px solid rgba(102, 187, 106, 0.3)',
                  borderRadius: 2,
                  borderLeft: '6px solid #66bb6a',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CheckCircleIcon sx={{ color: '#66bb6a', fontSize: 24 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#66bb6a',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '1rem' : '1.25rem',
                    }}
                  >
                    Antwort
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#ffffff',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {selectedQA.answer}
                </Typography>
              </Box>

              {/* Additional Metadata (if available) */}
              {selectedQA.created_at && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontSize: '0.75rem',
                    textAlign: 'center',
                  }}
                >
                  Veröffentlicht: {new Date(selectedQA.created_at).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'center', background: 'rgba(0, 0, 0, 0.2)' }}>
          <Button
            onClick={handleCloseQADialog}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              },
            }}
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <StyledContainer sx={{ position: 'relative', zIndex: 1 }}>
        {/* Cyber Weather Section */}
        <Slide direction="down" in={!loading} timeout={600}>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <SectionTitle variant="h4" component="h2">
                Cyberwetter
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Das Cyberwetter ist wie ein Wetterbericht fürs Internet. Es beobachtet verschiedene Dinge, die die Sicherheit im Netz betreffen. 
                    Aus all diesen Informationen erstellt das Cyberwetter eine Gesamtbewertung: Es zeigt, wie sicher oder riskant die aktuelle Lage im Internet ist.
                    So weißt du, ob die normalen Vorsichtsmaßnahmen ausreichen, oder du besonders aufpassen solltest.
                    <br/><br/>
                    Dieser Inhalt wird exklusiv von cyberlagebild.de erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {cyberWeather ? (
                  <Box>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', md: 'row' },
                      alignItems: { xs: 'flex-start', md: 'flex-start' },
                      justifyContent: 'space-between',
                      marginBottom: 3,
                      padding: { xs: 2, md: 3 },
                      background: 'linear-gradient(135deg, rgba(66, 165, 245, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%)',
                      borderRadius: 2,
                      border: '1px solid rgba(66, 165, 245, 0.3)',
                      gap: { xs: 2, md: 0 }
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: { xs: 1.5, md: 2 }, 
                        flex: 1,
                        flexDirection: { xs: 'column', sm: 'row' }
                      }}>
                        <Typography variant="h3" sx={{ 
                          fontSize: { xs: '2.5rem', md: '3rem' },
                          lineHeight: 1,
                          marginTop: 0.5,
                        }}>
                          {cyberWeather.weather_emoji}
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          color: '#ffffff',
                          lineHeight: 1.6,
                          fontSize: { xs: '1rem', md: '1.1rem' },
                          flex: 1,
                        }}>
                          {cyberWeather.weather_message}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        textAlign: { xs: 'left', md: 'right' }, 
                        marginLeft: { xs: 0, md: 2 },
                        marginTop: { xs: 1, md: 0 }
                      }}>
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          marginBottom: 1,
                          fontSize: { xs: '0.8rem', md: '0.875rem' }
                        }}>
                          Letzte Aktualisierung:
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: '#ffffff',
                          fontWeight: 500,
                          fontSize: { xs: '0.8rem', md: '0.875rem' }
                        }}>
                          {new Date(cyberWeather['@timestamp']).toLocaleString('de-DE')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Dynamic Threat Descriptions */}
                    {cyberWeather.dynamic_description && cyberWeather.dynamic_description.length > 0 && (
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography variant="h6" sx={{ 
                          color: '#42a5f5',
                          fontWeight: 600,
                          marginBottom: 2,
                        }}>
                          🚨 Aktuell größte Einflussfaktoren:
                        </Typography>
                        <Box component="ul" sx={{ 
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}>
                          {cyberWeather.dynamic_description.map((threat, index) => (
                            <Box component="li" key={index} sx={{
                              paddingLeft: 2,
                              position: 'relative',
                            }}>
                              <Box sx={{ 
                                position: 'absolute',
                                left: 0,
                                top: '0.5rem',
                                color: '#ff4444',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                              }}>
                                •
                              </Box>
                              <Box sx={{ paddingLeft: 1.5 }}>
                                <Typography variant="body2" sx={{ 
                                  color: '#ffffff',
                                  lineHeight: 1.5,
                                  marginBottom: 0.5,
                                }}>
                                  {threat.description}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: '#cccccc',
                                  fontSize: '0.8rem',
                                }}>
                                  Aktuelle Meldungen: {threat.count}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Threat Counts */}
                    <Box>
                      <Typography variant="h6" sx={{ 
                        color: '#42a5f5',
                        fontWeight: 600,
                        marginBottom: 2,
                      }}>
                        📊 Einflussfaktoren:
                      </Typography>
                      <Grid container spacing={{ xs: 1, md: 2 }} sx={{ 
                        '& .MuiGrid-item': {
                          paddingLeft: { xs: '4px', md: '8px' }
                        }
                      }}>
                        {Object.entries(cyberWeather.counts).map(([threat, count]) => {
                          const threatNames = {
                            'warn': 'Warnmeldungen',
                            'fake': 'Betrugsversuche',
                            'facts': 'Falschmeldungen',
                            'vuln': 'Sicherheitslücken',
                            'avail': 'Ausfälle',
                            'phish': 'Phishing'
                          };
                          const displayName = threatNames[threat] || threat;
                          
                          // Calculate percentage change (assuming we have previous values)
                          // For now, we'll show a placeholder. You'll need to provide previous values
                          const previousCount = cyberWeather.previous_counts?.[threat] || 0;
                          const percentageChange = previousCount > 0 
                            ? ((count - previousCount) / previousCount * 100).toFixed(1)
                            : count > 0 ? '+100' : '0';
                          const isPositive = count > previousCount;
                          const isNegative = count < previousCount;
                          
                          return (
                            <Grid item xs={6} sm={4} md={2} key={threat}>
                              <Box sx={{
                                padding: { xs: 1.5, md: 2 },
                                textAlign: 'center',
                                background: count > 0 ? 'rgba(255, 68, 68, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                                border: `1px solid ${count > 0 ? 'rgba(255, 68, 68, 0.3)' : 'rgba(76, 175, 80, 0.3)'}`,
                                borderRadius: 2,
                                minHeight: { xs: '80px', md: '100px' },
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                              }}>
                                <Typography variant="h4" sx={{ 
                                  color: count > 0 ? '#ff4444' : '#4CAF50',
                                  fontWeight: 'bold',
                                  marginBottom: 0.5,
                                  fontSize: { xs: '1.5rem', md: '2rem' }
                                }}>
                                  {count}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: '#ffffff',
                                  fontWeight: 500,
                                  marginBottom: 0.5,
                                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                                  lineHeight: 1.2
                                }}>
                                  {displayName}
                                </Typography>
                                {cyberWeather.previous_counts && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ 
                                      color: isPositive ? '#4CAF50' : isNegative ? '#ff4444' : '#cccccc',
                                      fontWeight: 500,
                                      fontSize: { xs: '0.65rem', md: '0.75rem' },
                                    }}>
                                      {isPositive ? '↗' : isNegative ? '↘' : '→'} {isPositive ? '+' : ''}{percentageChange}%
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    padding: 4,
                    color: '#42a5f5'
                  }}>
                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                      Keine Cyber Wetter Daten verfügbar
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cccccc' }}>
                      Aktuell sind keine Cyber Wetter Informationen verfügbar.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>

        {/* Current Week Calendar Events Section */}
        <Slide direction="up" in={!loading} timeout={800}>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Diese Woche - Sicherheitsveranstaltungen
                {calendarLoading && (
                  <CircularProgress 
                    size={20} 
                    sx={{ 
                      ml: 2, 
                      color: '#42a5f5',
                      display: 'inline-block'
                    }}
                  />
                )}
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Hier siehst du alle relevanten Sicherheitsveranstaltungen, Konferenzen und Schulungen für diese Woche. 
                    <br/><br/>
                    Aktuelle Kalenderwoche, von Montag bis Sonntag nach Datum sortiert.
                    <br/><br/>
                    Dieser Inhalt wird von uns und der Cyberlagebild Community erstellt und von unserem Team überprüft.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton size="small" sx={{ color: '#42a5f5' }}>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {currentWeekEvents && currentWeekEvents.length > 0 ? (
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: 'white', 
                      marginBottom: 2
                    }}>
                      Sicherheitsveranstaltungen für diese Woche (KW {getWeekNumber(new Date())}): {currentWeekEvents.length} Veranstaltungen
                    </Typography>
                    
                    <Grid container spacing={{ xs: 1, md: 2 }}>
                      {currentWeekEvents.map((event, index) => {
                        const eventId = event.id || index;
                        const isExpanded = expandedCards.has(eventId);
                        
                        return (
                          <Grid item xs={12} sm={6} md={4} key={eventId}>
                            <Card sx={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              backdropFilter: 'blur(10px)',
                              position: 'relative',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                transform: 'translateY(-2px)',
                                transition: 'all 0.3s ease'
                              }
                            }}
                            onClick={() => toggleCardExpansion(eventId)}>
                              {/* Event Type Chip in upper right corner */}
                              <Chip 
                                label={getEventTypeLabel(event.event_type)} 
                                size="small"
                                sx={{ 
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  backgroundColor: 'rgba(66, 165, 245, 0.2)',
                                  color: '#42a5f5',
                                  fontSize: '0.7rem',
                                  height: 20,
                                  zIndex: 1
                                }}
                              />
                              
                              <CardContent sx={{ 
                                padding: { xs: 1.5, md: 2 }, 
                                pt: { xs: 2, md: 3 },
                                '&:last-child': { paddingBottom: { xs: 1.5, md: 2 } }
                              }}>
                              {/* Event Title */}
                              {/* Always visible: Title, Logo, and Date */}
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                gap: 1, 
                                mb: isExpanded ? 2 : 0,
                                justifyContent: 'space-between',
                                flexDirection: { xs: 'column', sm: 'row' }
                              }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1, 
                                  flex: 1,
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  alignItems: { xs: 'flex-start', sm: 'center' }
                                }}>
                                  {event.organizer_logo && event.show_logo === true && (
                                    <img 
                                      src={`/api/v1/images/${event.organizer_logo}/data`}
                                      alt={`${event.organizer || 'Organizer'} Logo`}
                                      style={{ 
                                        width: '40px', 
                                        height: '40px', 
                                        objectFit: 'contain',
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                        display: 'block',
                                        border: '2px solid rgba(66, 165, 245, 0.5)',
                                        outline: 'none',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  )}
                                  <Box sx={{ flex: 1, width: '100%' }}>
                                    <Typography variant="h6" sx={{ 
                                      color: 'white', 
                                      fontSize: { xs: '0.9rem', md: '1rem' },
                                      fontWeight: 600,
                                      mb: 0.5,
                                      lineHeight: 1.3
                                    }}>
                                      {event.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                      color: '#42a5f5',
                                      fontSize: { xs: '0.75rem', md: '0.8rem' },
                                      fontWeight: 500,
                                      lineHeight: 1.2
                                    }}>
                                      {(() => {
                                        const startDate = new Date(event.start_date);
                                        const endDate = event.end_date ? new Date(event.end_date) : null;
                                        const startDateStr = startDate.toLocaleDateString('de-DE', {
                                        weekday: 'short',
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                        });
                                        const startTimeStr = startDate.toLocaleTimeString('de-DE', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                        });
                                        
                                        if (!endDate) {
                                          return `${startDateStr} ${startTimeStr} Uhr`;
                                        }
                                        
                                        // Check if event spans multiple days
                                        const startDay = new Date(startDate);
                                        startDay.setHours(0, 0, 0, 0);
                                        const endDay = new Date(endDate);
                                        endDay.setHours(0, 0, 0, 0);
                                        const isMultiDay = endDay > startDay;
                                        
                                        const endTimeStr = endDate.toLocaleTimeString('de-DE', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                        });
                                        
                                        if (isMultiDay) {
                                          const endDateStr = endDate.toLocaleDateString('de-DE', {
                                            weekday: 'short',
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric'
                                          });
                                          return `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr} Uhr`;
                                        } else {
                                          return `${startDateStr} ${startTimeStr} - ${endTimeStr} Uhr`;
                                        }
                                      })()}
                                    </Typography>
                                  </Box>
                                </Box>
                                
                                {/* Expand/Collapse Button */}
                                <IconButton 
                                  size="small"
                                  sx={{ 
                                    color: 'white',
                                    alignSelf: { xs: 'flex-end', sm: 'center' },
                                    marginTop: { xs: 1, sm: 0 },
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                  }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </Box>
                              
                              {/* Collapsible Content */}
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              
                              {/* Wann: Date and Time */}
                              <Box sx={{ mb: 1.5 }}>
                                <Typography variant="body2" sx={{ 
                                  color: '#42a5f5',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.75rem', md: '0.8rem' },
                                  mb: 0.5,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 0.5,
                                  flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 'fit-content' }}>
                                    <ScheduleIcon fontSize="small" />
                                    Wann:
                                  </Box>
                                  <span style={{ 
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.3
                                  }}>
                                    {(() => {
                                      const startDate = new Date(event.start_date);
                                      const endDate = event.end_date ? new Date(event.end_date) : null;
                                      const startDateStr = startDate.toLocaleDateString('de-DE', {
                                      weekday: 'long',
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                      });
                                      const startTimeStr = startDate.toLocaleTimeString('de-DE', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                      });
                                      
                                      if (!endDate) {
                                        return `${startDateStr} ${startTimeStr} Uhr`;
                                      }
                                      
                                      // Check if event spans multiple days
                                      const startDay = new Date(startDate);
                                      startDay.setHours(0, 0, 0, 0);
                                      const endDay = new Date(endDate);
                                      endDay.setHours(0, 0, 0, 0);
                                      const isMultiDay = endDay > startDay;
                                      
                                      const endTimeStr = endDate.toLocaleTimeString('de-DE', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                      });
                                      
                                      if (isMultiDay) {
                                        const endDateStr = endDate.toLocaleDateString('de-DE', {
                                          weekday: 'long',
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric'
                                        });
                                        return `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr} Uhr`;
                                      } else {
                                        return `${startDateStr} ${startTimeStr} - ${endTimeStr} Uhr`;
                                      }
                                    })()}
                                  </span>
                                </Typography>
                              </Box>
                              
                              {/* Wo: Location */}
                              {event.location && (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="body2" sx={{ 
                                    color: '#42a5f5',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}>
                                    <LocationIcon fontSize="small" />
                                    Wo: <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{event.location}</span>
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Wer: Organizer */}
                              <Box sx={{ mb: 1.5 }}>
                                <Typography variant="body2" sx={{ 
                                  color: '#42a5f5',
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}>
                                  <GroupsIcon fontSize="small" />
                                  Wer: <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                    {event.organizer || 'Cyberlagebild Community'}
                                  </span>
                                </Typography>
                              </Box>
                              
                              {/* Zielgruppe: Target Audience */}
                              <Box sx={{ mb: 1.5 }}>
                                <Typography variant="body2" sx={{ 
                                  color: '#42a5f5',
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}>
                                  <LanguageIcon fontSize="small" />
                                  Zielgruppe: <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                    {getTargetAudienceLabel(event.target_audience)}
                                  </span>
                                </Typography>
                              </Box>
                              
                              {/* Was: Description */}
                              {event.description && (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="body2" sx={{ 
                                    color: '#42a5f5',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}>
                                    <DescriptionIcon fontSize="small" />
                                    Was: <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{event.description}</span>
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Link: URL */}
                              {event.url && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="body2" sx={{ 
                                    color: '#42a5f5',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}>
                                    <LinkIcon fontSize="small" />
                                    Link: <Typography 
                                      component="a"
                                      href={event.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ 
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        textDecoration: 'underline',
                                        '&:hover': {
                                          color: '#2196f3'
                                        }
                                      }}
                                    >
                                      {event.url}
                                    </Typography>
                                  </Typography>
                                </Box>
                              )}
                              </Collapse>
                            </CardContent>
                          </Card>
                        </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: 200,
                    color: 'white',
                    fontStyle: 'italic'
                  }}>
                    <CalendarIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                      Keine Veranstaltungen für diese Woche geplant
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      textAlign: 'center', 
                      mt: 1,
                      opacity: 0.7
                    }}>
                      Schaue regelmäßig vorbei, um nichts zu
                    </Typography>
                  </Box>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>

        {/* Täglicher Sicherheitstipp Section */}
        <Slide direction="up" in={!loading} timeout={800}>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Täglicher Sicherheitstipp
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Jeden Tag gibt es einen neuen, praktischen Tipp zur Cybersicherheit. Diese Tipps helfen dir dabei, dich sicherer im Internet zu bewegen. Sie decken verschiedene Bereiche ab, von Passwort-Sicherheit über Soziale Netzwerke, bis hin zum Jugendschutz.
                    <br/><br/>
                    Dieser Inhalt wird exklusiv von cyberlagebild.de erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={24} sx={{ color: '#2196f3' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Lade täglichen Sicherheitstipp...
                    </Typography>
                  </Box>
                ) : error ? (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    <AlertTitle>Fehler</AlertTitle>
                    {error}
                  </Alert>
                ) : dailySecurityTip ? (
                  <>
                    <Box sx={{ position: 'relative' }}>
                      {/* Category Chip - responsive positioning */}
                      <Box sx={{ 
                        position: { xs: 'static', md: 'absolute' }, 
                        top: { xs: 'auto', md: 0 }, 
                        right: { xs: 'auto', md: 0 }, 
                        zIndex: 1,
                        marginBottom: { xs: 2, md: 0 },
                        display: 'flex',
                        justifyContent: { xs: 'flex-start', md: 'flex-end' }
                      }}>
                        <StyledChip 
                          label={`Kategorie: ${dailySecurityTip.category}`}
                          sx={{ 
                            background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                            color: '#ffffff',
                            fontWeight: 'bold',
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            height: { xs: 24, md: 32 }
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        marginBottom: 2,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 1, sm: 2 }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LightbulbIcon sx={{ color: '#FFD700', fontSize: { xs: 24, md: 32 } }} />
                          <Typography variant="h5" sx={{ 
                            fontWeight: 'bold',
                            color: '#42a5f5',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            fontSize: { xs: '1.1rem', md: '1.5rem' },
                            lineHeight: 1.2
                          }}>
                            Täglicher Sicherheitstipp - {dailySecurityTip.date}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ 
                        color: '#ffffff',
                        marginBottom: 2,
                        fontWeight: 600,
                      }}>
                        {dailySecurityTip.tip}
                      </Typography>
                </Box>
                {dailySecurityTip.description && (
                  <Typography variant="body2" sx={{ 
                    color: '#ffffff', 
                    fontStyle: 'italic',
                    padding: { xs: 1.5, md: 2 },
                    background: 'rgba(66, 165, 245, 0.1)',
                    borderRadius: 2,
                    border: '1px solid rgba(66, 165, 245, 0.3)',
                    fontSize: { xs: '0.85rem', md: '0.875rem' },
                    lineHeight: 1.5
                  }}>
                    <strong>Warum ist das wichtig?</strong> 
                    <br />
                    {dailySecurityTip.description}
                  </Typography>
                )}

              </>
            ) : (
              <Typography variant="body1">Keine Sicherheitstipps verfügbar</Typography>
            )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>

        {/* Aktuelle Warnmeldungen für Deutschland */}
        <Slide direction="up" in={!loading} timeout={800}>
          <Box sx={{ marginBottom: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Aktuelle Warnmeldungen für Deutschland
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Hier siehst du aktuelle Warnungen des Bundesamts für Bevölkerungsschutz und Katastrophenhilfe (BBK). Diese Warnungen informieren über Gefahren wie Unwetter, Hochwasser, Bombenfunde oder andere Notfälle in verschiedenen Regionen Deutschlands. Die Karte zeigt dir genau, welche Gebiete betroffen sind.
                    <br/><br/>
                    Dieser Inhalt wird vom Bundesamt für Bevölkerungsschutz und Katastrophenhilfe (BBK) erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            {/* Map Section */}
            <Box sx={{ marginBottom: 4 }}>
              <Typography variant="h6" sx={{ 
                color: '#ffffff',
                marginBottom: 2,
                fontWeight: 600,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
              }}>
                📍 Betroffene Gebiete auf der Karte
              </Typography>
              
              <DashboardCard elevation={0}>
                <Box sx={{ 
                  height: { xs: 300, sm: 400, md: 500 },
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  '& .leaflet-container': {
                    background: '#1a1a1a',
                  },
                  '& .leaflet-popup-content-wrapper': {
                    background: '#1a1a1a',
                    color: '#ffffff',
                    borderRadius: 8,
                  },
                  '& .leaflet-popup-tip': {
                    background: '#1a1a1a',
                  },
                }}>
                  <MapContainer
                    key="germany-map"
                    center={[51.1657, 10.4515]} // Center of Germany
                    zoom={6}
                    minZoom={5}
                    maxZoom={10}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={true}
                    scrollWheelZoom={true}
                    maxBounds={[
                      [45.0, 3.0], // Southwest corner (slightly beyond Germany)
                      [57.0, 18.0]  // Northeast corner (slightly beyond Germany)
                    ]}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {/* Warning Markers and Polygons */}
                    {ninaWarnings && ninaWarnings.length > 0 && ninaWarnings.map((warning, warningIndex) => (
                      warning.areas && warning.areas.map((area, areaIndex) => {
                        const key = `${warning.id}-${areaIndex}`;
                        
                        // Create polygon coordinates if available
                        const polygonCoords = area.coordinates && area.coordinates.length > 0 
                          ? area.coordinates.map(coord => [coord.lat, coord.lng])
                          : null;
                        
                        // Use center point for marker if no polygon
                        const centerPoint = area.center || { lat: 51.1657, lng: 10.4515 };
                        
                        return (
                          <React.Fragment key={key}>
                            {/* Polygon for area coverage */}
                            {polygonCoords && polygonCoords.length > 3 && (
                              <Polygon
                                positions={polygonCoords}
                                pathOptions={{
                                  color: '#ff4444',
                                  fillColor: '#ff4444',
                                  fillOpacity: 0.3,
                                  weight: 2,
                                }}
                              >
                                <Popup 
                                  maxWidth={isMobile ? 280 : 400} 
                                  maxHeight={isMobile ? 300 : 300}
                                  closeButton={true}
                                  autoClose={true}
                                  closeOnClick={true}
                                >
                                  <Box sx={{ 
                                    minWidth: isMobile ? 280 : 200, 
                                    maxWidth: isMobile ? 320 : 400,
                                    maxHeight: isMobile ? 350 : 300,
                                    overflow: 'auto',
                                    padding: isMobile ? 2.5 : 0,
                                    backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.9)' : 'transparent',
                                    borderRadius: isMobile ? 2 : 0,
                                    // Enhanced mobile scrolling
                                    WebkitOverflowScrolling: 'touch',
                                    overscrollBehavior: 'contain',
                                    '&::-webkit-scrollbar': {
                                      width: isMobile ? '10px' : '6px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                      background: 'rgba(255, 255, 255, 0.2)',
                                      borderRadius: '6px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                      background: 'rgba(255, 68, 68, 0.8)',
                                      borderRadius: '6px',
                                      minHeight: isMobile ? '50px' : '20px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                      background: 'rgba(255, 68, 68, 1)',
                                    },
                                    // Better touch targets for mobile
                                    '& *': {
                                      touchAction: 'pan-y',
                                    }
                                  }}>
                                    <Typography variant="h6" sx={{ 
                                      color: '#ff4444', 
                                      fontWeight: 700, 
                                      marginBottom: { xs: 2, md: 1 },
                                      fontSize: { xs: '1.4rem', md: '1rem' },
                                      lineHeight: 1.2,
                                      wordBreak: 'break-word',
                                      textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none'
                                    }}>
                                      ⚠️ {warning.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                      color: '#ffffff', 
                                      marginBottom: { xs: 2, md: 1 },
                                      fontSize: { xs: '1.1rem', md: '0.875rem' },
                                      wordBreak: 'break-word',
                                      lineHeight: 1.5,
                                      fontWeight: isMobile ? 500 : 400,
                                      textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none'
                                    }}>
                                      <strong style={{ color: '#ffcc00', fontSize: isMobile ? '1.1rem' : '0.875rem' }}>Gebiet:</strong> {area.description}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                      color: '#ffffff', 
                                      marginBottom: { xs: 2, md: 1 },
                                      fontSize: { xs: '1.1rem', md: '0.875rem' },
                                      wordBreak: 'break-word',
                                      lineHeight: 1.5,
                                      fontWeight: isMobile ? 500 : 400,
                                      textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none'
                                    }}>
                                      <strong style={{ color: '#ffcc00', fontSize: isMobile ? '1.1rem' : '0.875rem' }}>Schweregrad:</strong> {warning.severity}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                      color: '#ffffff', 
                                      marginBottom: { xs: 2, md: 1 },
                                      fontSize: { xs: '1.1rem', md: '0.875rem' },
                                      wordBreak: 'break-word',
                                      lineHeight: 1.5,
                                      fontWeight: isMobile ? 500 : 400,
                                      textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none'
                                    }}>
                                      <strong style={{ color: '#ffcc00', fontSize: isMobile ? '1.1rem' : '0.875rem' }}>Dringlichkeit:</strong> {warning.urgency}
                                    </Typography>
                                    {warning.description && (
                                      <Typography variant="body2" sx={{ 
                                        color: '#e0e0e0', 
                                        fontSize: { xs: '1.1rem', md: '0.9rem' },
                                        lineHeight: 1.6,
                                        wordBreak: 'break-word',
                                        whiteSpace: 'pre-wrap',
                                        marginTop: { xs: 2, md: 0.5 },
                                        fontWeight: isMobile ? 400 : 300,
                                        textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none',
                                        backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                        padding: isMobile ? 1.5 : 0,
                                        borderRadius: isMobile ? 1 : 0
                                      }}>
                                        {warning.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </Popup>
                              </Polygon>
                            )}
                            
                            {/* Marker for center point */}
                            <Marker
                              position={[centerPoint.lat, centerPoint.lng]}
                              icon={L.divIcon({
                                className: 'custom-marker',
                                html: `<div style="
                                  background: linear-gradient(135deg, #ff4444 0%, #cc0000 100%);
                                  width: 20px;
                                  height: 20px;
                                  border-radius: 50%;
                                  border: 2px solid #ffffff;
                                  box-shadow: 0 0 10px rgba(255, 68, 68, 0.6);
                                  animation: pulse 2s infinite;
                                "></div>`,
                                iconSize: [20, 20],
                                iconAnchor: [10, 10],
                              })}
                            >
                              <Popup 
                                maxWidth={isMobile ? 280 : 400} 
                                maxHeight={isMobile ? 300 : 300}
                                closeButton={true}
                                autoClose={true}
                                closeOnClick={true}
                              >
                                <Box sx={{ 
                                  minWidth: isMobile ? 280 : 200, 
                                  maxWidth: isMobile ? 320 : 400,
                                  maxHeight: isMobile ? 350 : 300,
                                  overflow: 'auto',
                                  padding: isMobile ? 2.5 : 0,
                                  backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.9)' : 'transparent',
                                  borderRadius: isMobile ? 2 : 0,
                                  // Enhanced mobile scrolling
                                  WebkitOverflowScrolling: 'touch',
                                  overscrollBehavior: 'contain',
                                  '&::-webkit-scrollbar': {
                                    width: isMobile ? '10px' : '6px',
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    borderRadius: '6px',
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    background: 'rgba(255, 68, 68, 0.8)',
                                    borderRadius: '6px',
                                    minHeight: isMobile ? '50px' : '20px',
                                  },
                                  '&::-webkit-scrollbar-thumb:hover': {
                                    background: 'rgba(255, 68, 68, 1)',
                                  },
                                  // Better touch targets for mobile
                                  '& *': {
                                    touchAction: 'pan-y',
                                  }
                                }}>
                                  <Typography variant="h6" sx={{ 
                                    color: '#ff4444', 
                                    fontWeight: 700, 
                                    marginBottom: { xs: 2, md: 1 },
                                    fontSize: { xs: '1.4rem', md: '1rem' },
                                    lineHeight: 1.2,
                                    wordBreak: 'break-word',
                                    textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none'
                                  }}>
                                    ⚠️ {warning.title}
                                  </Typography>
                                  <Typography variant="body2" sx={{ 
                                    color: '#ffffff', 
                                    marginBottom: { xs: 2, md: 1 },
                                    fontSize: { xs: '1.1rem', md: '0.875rem' },
                                    wordBreak: 'break-word',
                                    lineHeight: 1.5,
                                    fontWeight: isMobile ? 500 : 400,
                                    textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none'
                                  }}>
                                    <strong style={{ color: '#ffcc00', fontSize: isMobile ? '1.1rem' : '0.875rem' }}>Gebiet:</strong> {area.description}
                                  </Typography>
                                  <Typography variant="body2" sx={{ 
                                    color: '#ffffff', 
                                    marginBottom: { xs: 2, md: 1 },
                                    fontSize: { xs: '1.1rem', md: '0.875rem' },
                                    wordBreak: 'break-word',
                                    lineHeight: 1.5,
                                    fontWeight: isMobile ? 500 : 400,
                                    textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none'
                                  }}>
                                    <strong style={{ color: '#ffcc00', fontSize: isMobile ? '1.1rem' : '0.875rem' }}>Schweregrad:</strong> {warning.severity}
                                  </Typography>
                                  <Typography variant="body2" sx={{ 
                                    color: '#ffffff', 
                                    marginBottom: { xs: 2, md: 1 },
                                    fontSize: { xs: '1.1rem', md: '0.875rem' },
                                    wordBreak: 'break-word',
                                    lineHeight: 1.5,
                                    fontWeight: isMobile ? 500 : 400,
                                    textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none'
                                  }}>
                                    <strong style={{ color: '#ffcc00', fontSize: isMobile ? '1.1rem' : '0.875rem' }}>Dringlichkeit:</strong> {warning.urgency}
                                  </Typography>
                                  {warning.description && (
                                    <Typography variant="body2" sx={{ 
                                      color: '#e0e0e0', 
                                      fontSize: { xs: '1.1rem', md: '0.9rem' },
                                      lineHeight: 1.6,
                                      wordBreak: 'break-word',
                                      whiteSpace: 'pre-wrap',
                                      marginTop: { xs: 2, md: 0.5 },
                                      fontWeight: isMobile ? 400 : 300,
                                      textShadow: isMobile ? '0 1px 2px rgba(0, 0, 0, 0.8)' : 'none',
                                      backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                      padding: isMobile ? 1.5 : 0,
                                      borderRadius: isMobile ? 1 : 0
                                    }}>
                                      {warning.description}
                                    </Typography>
                                  )}
                                </Box>
                              </Popup>
                            </Marker>
                          </React.Fragment>
                        );
                      })
                    ))}
                  </MapContainer>
                  
                  {/* Map Legend */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: { xs: 8, md: 16 },
                    right: { xs: 8, md: 16 },
                    left: { xs: 8, md: 'auto' },
                    background: 'rgba(0, 0, 0, 0.8)',
                    padding: { xs: 1.5, md: 2 },
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    zIndex: 1000,
                    maxWidth: { xs: 'calc(100% - 16px)', md: 'auto' }
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#ffffff', 
                      fontWeight: 600, 
                      marginBottom: 1,
                      fontSize: { xs: '0.75rem', md: '0.875rem' }
                    }}>
                      Legende:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 0.5 }}>
                      <Box sx={{
                        width: { xs: 10, md: 12 },
                        height: { xs: 10, md: 12 },
                        background: 'linear-gradient(135deg, #ff4444 0%, #cc0000 100%)',
                        borderRadius: '50%',
                        marginRight: 1,
                      }} />
                      <Typography variant="body2" sx={{ 
                        color: '#ffffff', 
                        fontSize: { xs: '0.7rem', md: '0.75rem' }
                      }}>
                        Aktive Warnung
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ 
                      color: '#ffffff', 
                      fontSize: { xs: '0.7rem', md: '0.75rem' }, 
                      marginTop: 1 
                    }}>
                      {ninaWarnings ? ninaWarnings.length : 0} aktive Warnungen
                    </Typography>
                  </Box>
                </Box>
              </DashboardCard>
            </Box>
          </Box>
        </Slide>

        {/* Community Reports Section */}
        <Slide direction="up" in={!loading} timeout={1000}>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Warnungen aus der Community
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Hier siehst du Berichte von Nutzer:innen unserer Community, die bereits von unserem Team überprüft wurden. Diese Warnungen helfen dir dabei, aktuelle Bedrohungen und betrügerische Aktivitäten zu erkennen und zu vermeiden.
                    <br/><br/>
                    Dieser Inhalt wird von der Cyberlagebild Community erstellt und von unserem Team überprüft.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {communityReports && communityReports.length > 0 ? (
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: '#42a5f5', 
                      fontWeight: 600, 
                      marginBottom: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: '1rem', md: '1.25rem' }
                    }}>
                      Überprüfte Community-Berichte ({communityReports.length})
                    </Typography>
                    
                    <Box sx={{ 
                      maxHeight: { xs: 400, md: 500 }, 
                      overflowY: 'auto',
                      paddingRight: { xs: 1, md: 0 },
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(66, 165, 245, 0.5)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: 'rgba(66, 165, 245, 0.7)',
                        },
                      },
                    }}>
                      {communityReports.map((report, index) => {
                        // Get report type display name
                        const typeNames = {
                          'fake_webshop': 'Fake Webshop',
                          'phishing': 'Phishing',
                          'malware': 'Malware',
                          'scam': 'Betrug',
                          'other': 'Sonstiges'
                        };
                        const displayType = typeNames[report.report_type] || report.report_type;
                        
                        return (
                          <Box key={report.id || index} sx={{
                            padding: { xs: 1.5, md: 2 },
                            marginBottom: 2,
                            background: 'rgba(66, 165, 245, 0.1)',
                            border: '1px solid rgba(66, 165, 245, 0.3)',
                            borderRadius: 2,
                            borderLeft: '4px solid #42a5f5',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateX(4px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            }
                          }}>
                            <Box sx={{ marginBottom: 1 }}>
                              <Typography variant="subtitle1" sx={{ 
                                color: '#ffffff', 
                                fontWeight: 600,
                                fontSize: { xs: '0.9rem', md: '1rem' }
                              }}>
                                {displayType}
                              </Typography>
                            </Box>
                            
                            <Typography variant="body2" sx={{ 
                              color: '#ffffff',
                              fontSize: { xs: '0.8rem', md: '0.9rem' },
                              lineHeight: 1.4,
                              marginBottom: 1
                            }}>
                              {report.description}
                            </Typography>
                            
                            {report.url && (
                              <Box sx={{ 
                                background: 'rgba(255, 68, 68, 0.1)', 
                                border: '1px solid rgba(255, 68, 68, 0.3)',
                                borderRadius: 1,
                                padding: { xs: 0.75, md: 1 },
                                marginBottom: 1
                              }}>
                                <Typography variant="body2" sx={{ 
                                  color: '#ff4444',
                                  fontWeight: 600,
                                  marginBottom: 0.5,
                                  fontSize: { xs: '0.75rem', md: '0.85rem' }
                                }}>
                                  Betroffene URL:
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: '#ffffff',
                                  fontSize: { xs: '0.75rem', md: '0.85rem' },
                                  lineHeight: 1.4,
                                  wordBreak: 'break-all'
                                }}>
                                  {report.url}
                                </Typography>
                              </Box>
                            )}
                            
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              marginTop: 1,
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: { xs: 0.5, sm: 0 }
                            }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: { xs: 1, sm: 2 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' }
                              }}>
                                <Typography variant="body2" sx={{ 
                                  color: 'white',
                                  fontSize: { xs: '0.7rem', md: '0.8rem' }
                                }}>
                                  <strong>Eingereicht:</strong> {report.created_at ? new Date(report.created_at).toLocaleDateString('de-DE') : 'Unbekannt'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: 'white',
                                  fontSize: { xs: '0.7rem', md: '0.8rem' }
                                }}>
                                  <strong>Status:</strong> Überprüft
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    padding: 4,
                    color: '#42a5f5'
                  }}>
                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                      Keine überprüften Berichte verfügbar
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Aktuell sind keine überprüften Community-Berichte verfügbar.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>

        {/* Phishing Warnings Section */}
        <Slide direction="up" in={!loading} timeout={1000}>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Aktuelle Phishing-Warnungen
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Hier findest du die neuesten Phishing-Warnungen der letzten 24 Stunden. Phishing-Warnungen sollen dich vor Betrügern schützen, die deine Anmeldedaten stehlen wollen.
                    <br/><br/>
                    Dieser Inhalt wird von der Verbraucherzentrale Nordrhein-Westfalen (VZ NRW) erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {phishingWarnings && phishingWarnings.warnings && phishingWarnings.warnings.length > 0 ? (
                  <List>
                    {phishingWarnings.warnings.map((warning, index) => (
                      <StyledListItem key={index} sx={{ paddingLeft: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                        <ListItemText 
                          primary={
                            <Box>
                              <Typography variant="h6" sx={{ 
                                color: '#42a5f5', 
                                marginBottom: 1.5,
                                fontWeight: 600,
                                textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                                fontSize: { xs: '1rem', md: '1.25rem' },
                                lineHeight: 1.3
                              }}>
                                {warning.companies && warning.companies.length > 0 ? warning.companies.join(', ') : 'Unbekannt'} - {warning.theme}
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                color: '#ffffff', 
                                marginBottom: 2,
                                lineHeight: 1.6,
                                fontSize: { xs: '0.85rem', md: '0.875rem' }
                              }}>
                                {warning.description.replace(/https?:\/\/[^\s]+/g, '').replace(/\s+/g, ' ').trim() + ' ' + warning.post_url}
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                gap: { xs: 1, md: 2 }, 
                                flexWrap: 'wrap',
                                flexDirection: { xs: 'column', sm: 'row' }
                              }}>
                                <StyledChip 
                                  label={warning.companies && warning.companies.length > 0 ? warning.companies.join(', ') : 'Unbekannt'}
                                  sx={{ 
                                    background: 'linear-gradient(135deg, #333 0%, #444 100%)',
                                    color: '#ffffff',
                                    fontWeight: 500,
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    height: { xs: 24, md: 32 }
                                  }}
                                />
                                <StyledChip 
                                  label={warning.theme}
                                  sx={{ 
                                    background: 'linear-gradient(135deg, #444 0%, #555 100%)',
                                    color: '#ffffff',
                                    fontWeight: 500,
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    height: { xs: 24, md: 32 }
                                  }}
                                />
                                <StyledChip 
                                  label={new Date(warning.timestamp).toLocaleString('de-DE')}
                                  sx={{ 
                                    background: 'linear-gradient(135deg, #555 0%, #666 100%)',
                                    color: '#ffffff',
                                    fontWeight: 500,
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    height: { xs: 24, md: 32 }
                                  }}
                                />
                              </Box>
                            </Box>
                          }
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: '#ffffff',
                              width: '100%'
                            }
                          }}
                        />
                      </StyledListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1" sx={{ 
                    color: '#ffffff', 
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: 4,
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                    border: '1px dashed rgba(255, 255, 255, 0.2)',
                  }}>
                    Keine Phishing-Warnungen in den letzten 24 Stunden.
                  </Typography>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>

        {/* Aktuelle Betrugsversuche Section */}
        <Slide direction="up" in={!loading} mountOnEnter unmountOnExit>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Aktuelle Betrugsversuche
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Hier findest du aktuelle Warnungen vor betrügerischen Online-Shops und anderen Betrugsversuchen. Diese Informationen werden regelmäßig aktualisiert und zeigen dir, welche Websites oder Angebote du meiden solltest. Die Warnungen helfen dir dabei, nicht auf Betrugsversuche hereinzufallen.
                    <br/><br/>
                    Dieser Inhalt wird von der Verbraucherzentrale Hamburg (VZHH) erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {fakeShops && fakeShops.length > 0 ? (
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: '#ff4444', 
                      fontWeight: 600, 
                      marginBottom: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: '1rem', md: '1.25rem' }
                    }}>
                      ⚠️ Neue Fake-Shop Warnungen ({fakeShops.length})
                    </Typography>
                    
                    <Box sx={{ 
                      maxHeight: { xs: 300, md: 400 }, 
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(66, 165, 245, 0.5)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: 'rgba(66, 165, 245, 0.7)',
                        },
                      },
                    }}>
                      {fakeShops.map((shop, index) => (
                        <Box key={shop.id} sx={{
                          padding: { xs: 1.5, md: 2 },
                          marginBottom: 2,
                          background: 'rgba(255, 68, 68, 0.1)',
                          border: '1px solid rgba(255, 68, 68, 0.3)',
                          borderRadius: 2,
                          borderLeft: '4px solid #ff4444',
                        }}>
                          <Typography variant="subtitle1" sx={{ 
                            color: '#ff4444', 
                            fontWeight: 600,
                            marginBottom: 1,
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          }}>
                            🛒 {shop.domain}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#ffffff',
                            marginBottom: 1,
                            fontSize: { xs: '0.8rem', md: '0.875rem' }
                          }}>
                            <strong>Kategorie:</strong> {shop.category}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#cccccc',
                            fontSize: { xs: '0.75rem', md: '0.9rem' }
                          }}>
                            <strong>Gelistet am:</strong> {shop.date_listed}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    padding: 4,
                    color: '#42a5f5'
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 48, marginBottom: 2 }} />
                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                      Keine neuen Betrugsversuche
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      In den letzten 24 Stunden wurden keine neuen Betrugsversuche gemeldet.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>

        {/* Faktenchecks Section */}
        <Slide direction="up" in={!loading} mountOnEnter unmountOnExit>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Faktenchecks
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Hier findest du aktuelle Faktenchecks zur Bekämpfung von Falschmeldungen. Diese Faktenchecks überprüfen Behauptungen und Nachrichten auf ihre Richtigkeit und helfen dir dabei, zwischen wahren und falschen Informationen zu unterscheiden.
                    <br/><br/>
                    Dieser Inhalt wird von Mimikama - Verein zur Aufklärung über Internetmissbrauch (mimikama.org) erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {mimikamaFaktenchecks && mimikamaFaktenchecks.length > 0 ? (
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: '#42a5f5', 
                      fontWeight: 600, 
                      marginBottom: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: '1rem', md: '1.25rem' }
                    }}>
                      📰 Neueste Faktenchecks der letzten 24 Stunden ({mimikamaFaktenchecks.length})
                    </Typography>
                    
                    <Box sx={{ 
                      maxHeight: { xs: 400, md: 500 }, 
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(66, 165, 245, 0.5)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: 'rgba(66, 165, 245, 0.7)',
                        },
                      },
                    }}>
                      {mimikamaFaktenchecks.map((faktencheck, index) => {
                        // Determine color based on faktencheck_rating
                        let borderColor = '#42a5f5'; // default blue
                        let ratingText = 'Unbekannt';
                        
                        if (faktencheck.faktencheck_rating === 'FALSCH') {
                          borderColor = '#ff4444';
                          ratingText = 'Falsch';
                        } else if (faktencheck.faktencheck_rating === 'WAHR') {
                          borderColor = '#4CAF50';
                          ratingText = 'Wahr';
                        } else if (faktencheck.faktencheck_rating === 'VORSICHT') {
                          borderColor = '#FFD700';
                          ratingText = 'Vorsicht';
                        }
                        
                        return (
                          <Box key={faktencheck.id || index} sx={{
                            padding: { xs: 1.5, md: 2 },
                            marginBottom: 2,
                            background: 'rgba(66, 165, 245, 0.1)',
                            border: '1px solid rgba(66, 165, 245, 0.3)',
                            borderRadius: 2,
                            borderLeft: `4px solid ${borderColor}`,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateX(4px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            }
                          }}>
                            <Box sx={{ marginBottom: 1 }}>
                              <Typography variant="subtitle1" sx={{ 
                                color: '#ffffff', 
                                fontWeight: 600,
                                flex: 1,
                                fontSize: { xs: '0.9rem', md: '1rem' },
                                lineHeight: 1.3
                              }}>
                                🔍 {faktencheck.title}
                              </Typography>
                            </Box>
                            
                            {faktencheck.claim && (
                              <Box sx={{ 
                                background: `${borderColor}15`, 
                                border: `1px solid ${borderColor}50`,
                                borderRadius: 1,
                                padding: { xs: 0.75, md: 1 },
                                marginBottom: 1
                              }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'flex-start', 
                                  marginBottom: 1,
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  gap: { xs: 0.5, sm: 0 }
                                }}>
                                  <Typography variant="body2" sx={{ 
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.75rem', md: '0.85rem' }
                                  }}>
                                    Behauptung:
                                  </Typography>
                                  <Chip 
                                    label={ratingText}
                                    sx={{ 
                                      backgroundColor: borderColor,
                                      color: '#ffffff',
                                      fontWeight: 'bold',
                                      fontSize: { xs: '0.7rem', md: '0.8rem' },
                                      minWidth: { xs: '60px', md: '80px' },
                                      height: { xs: 20, md: 24 }
                                    }}
                                  />
                                </Box>
                                <Typography variant="body2" sx={{ 
                                  color: '#ffffff',
                                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                                  lineHeight: 1.4
                                }}>
                                  {faktencheck.claim}
                                </Typography>
                              </Box>
                            )}
                            
                            {faktencheck.faktencheck && (
                              <Box sx={{ 
                                background: 'rgba(76, 175, 80, 0.1)', 
                                border: '1px solid rgba(76, 175, 80, 0.3)',
                                borderRadius: 1,
                                padding: { xs: 0.75, md: 1 },
                                marginBottom: 1
                              }}>
                                <Typography variant="body2" sx={{ 
                                  color: '#4CAF50',
                                  fontWeight: 600,
                                  marginBottom: 0.5,
                                  fontSize: { xs: '0.75rem', md: '0.85rem' }
                                }}>
                                  ✅ Faktencheck:
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: '#ffffff',
                                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                                  lineHeight: 1.4
                                }}>
                                  {faktencheck.faktencheck}
                                </Typography>
                              </Box>
                            )}
                            
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              marginTop: 1,
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: { xs: 1, sm: 0 }
                            }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: { xs: 1, sm: 2 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' }
                              }}>
                                <Typography variant="body2" sx={{ 
                                  color: 'white',
                                  fontSize: { xs: '0.7rem', md: '0.8rem' }
                                }}>
                                  <strong>Veröffentlicht:</strong> {faktencheck.timestamp ? new Date(faktencheck.timestamp).toLocaleDateString('de-DE') : 'Unbekannt'}
                                </Typography>
                                {faktencheck.author && (
                                  <Typography variant="body2" sx={{ 
                                    color: 'white',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' }
                                  }}>
                                    <strong>Autor:</strong> {faktencheck.author}
                                  </Typography>
                                )}
                              </Box>
                              
                              {faktencheck.url && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  href={faktencheck.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: 'white',
                                    borderColor: 'white',
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    padding: { xs: '3px 6px', md: '4px 8px' },
                                    minWidth: 'auto',
                                    alignSelf: { xs: 'flex-end', sm: 'auto' },
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                      borderColor: '#e3f2fd',
                                      color: '#e3f2fd',
                                    }
                                  }}
                                >
                                  Zum Artikel →
                                </Button>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    padding: 4,
                    color: '#42a5f5'
                  }}>
                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                      Keine neuen Faktenchecks
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cccccc' }}>
                      In den letzten 24 Stunden wurden keine neuen Faktenchecks von Mimikama veröffentlicht.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>

        {/* BürgerCERT Section */}
        <Slide direction="up" in={!loading} mountOnEnter unmountOnExit>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                BürgerCERT Sicherheitswarnungen
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Hier findest du aktuelle Sicherheitswarnungen des Bundesamts für Sicherheit in der Informationstechnik (BSI). BürgerCERT informiert über kritische Sicherheitslücken in Software und Hardware, die deine Geräte und Daten gefährden könnten. Diese Warnungen helfen dir dabei, deine Systeme zu schützen.
                    <br/><br/>
                    Dieser Inhalt wird vom Bundesamt für Sicherheit in der Informationstechnik (BSI) erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {buergercertEntries && buergercertEntries.length > 0 ? (
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: '#42a5f5', 
                      fontWeight: 600, 
                      marginBottom: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: '1rem', md: '1.25rem' }
                    }}>
                      🚨 Neue Sicherheitswarnungen der letzten 24 Stunden ({buergercertEntries.length})
                    </Typography>
                    
                    <Box sx={{ 
                      maxHeight: { xs: 400, md: 500 }, 
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(66, 165, 245, 0.5)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: 'rgba(66, 165, 245, 0.7)',
                        },
                      },
                    }}>
                      {buergercertEntries.map((entry, index) => (
                        <Box key={entry.id || index} sx={{
                          padding: { xs: 1.5, md: 2 },
                          marginBottom: 2,
                          background: 'rgba(255, 152, 0, 0.1)',
                          border: '1px solid rgba(255, 152, 0, 0.3)',
                          borderRadius: 2,
                          borderLeft: '4px solid #FF9800',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateX(4px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                          }
                        }}>
                          <Typography variant="subtitle1" sx={{ 
                            color: '#ffffff', 
                            fontWeight: 600,
                            marginBottom: 1,
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            lineHeight: 1.3
                          }}>
                            🛡️ {entry.title}
                          </Typography>
                          
                          {entry.risiko && (
                            <Box sx={{ 
                              display: 'inline-block',
                              marginBottom: 1
                            }}>
                              <Chip 
                                label={`Risiko: ${entry.risiko}`}
                                sx={{ 
                                  backgroundColor: entry.risiko === 'KRITISCH' ? '#ff4444' : 
                                                 entry.risiko === 'HOCH' ? '#ff9800' : 
                                                 entry.risiko === 'MITTEL' ? '#ffeb3b' : '#4CAF50',
                                  color: '#ffffff',
                                  fontWeight: 'bold',
                                  fontSize: { xs: '0.65rem', md: '0.75rem' },
                                  height: { xs: 20, md: 24 }
                                }}
                              />
                            </Box>
                          )}
                          
                          {entry.beschreibung && (
                            <Typography variant="body2" sx={{ 
                              color: '#ffffff',
                              fontSize: { xs: '0.8rem', md: '0.9rem' },
                              lineHeight: 1.4,
                              marginBottom: 1
                            }}>
                              {entry.beschreibung}
                            </Typography>
                          )}
                          
                          {entry.summary && (
                            <Typography variant="body2" sx={{ 
                              color: '#cccccc',
                              fontSize: { xs: '0.75rem', md: '0.85rem' },
                              lineHeight: 1.4,
                              marginBottom: 1,
                              fontStyle: 'italic'
                            }}>
                              {entry.summary}
                            </Typography>
                          )}
                          
                          {entry.betroffene_systeme_liste && (
                            <Box sx={{ 
                              background: 'rgba(255, 68, 68, 0.1)', 
                              border: '1px solid rgba(255, 68, 68, 0.3)',
                              borderRadius: 1,
                              padding: { xs: 0.75, md: 1 },
                              marginBottom: 1
                            }}>
                              <Typography variant="body2" sx={{ 
                                color: '#ff4444',
                                fontWeight: 600,
                                marginBottom: 0.5,
                                fontSize: { xs: '0.75rem', md: '0.85rem' }
                              }}>
                                Betroffene Systeme:
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                color: '#ffffff',
                                fontSize: { xs: '0.75rem', md: '0.85rem' },
                                lineHeight: 1.4
                              }}>
                                {entry.betroffene_systeme_liste}
                              </Typography>
                            </Box>
                          )}
                          
                          {entry.empfehlung && (
                            <Box sx={{ 
                              background: 'rgba(76, 175, 80, 0.1)', 
                              border: '1px solid rgba(76, 175, 80, 0.3)',
                              borderRadius: 1,
                              padding: { xs: 0.75, md: 1 },
                              marginBottom: 1
                            }}>
                              <Typography variant="body2" sx={{ 
                                color: '#4CAF50',
                                fontWeight: 600,
                                marginBottom: 0.5,
                                fontSize: { xs: '0.75rem', md: '0.85rem' }
                              }}>
                                Empfehlung:
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                color: '#ffffff',
                                fontSize: { xs: '0.75rem', md: '0.85rem' },
                                lineHeight: 1.4
                              }}>
                                {entry.empfehlung}
                              </Typography>
                            </Box>
                          )}
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 1 }}>
                            <Typography variant="body2" sx={{ 
                              color: '#aaaaaa',
                              fontSize: { xs: '0.7rem', md: '0.8rem' }
                            }}>
                              <strong>Veröffentlicht:</strong> {entry.published ? new Date(entry.published).toLocaleDateString('de-DE') : 'Unbekannt'}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    padding: 4,
                    color: '#42a5f5'
                  }}>
                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                      Keine neuen Sicherheitswarnungen
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      In den letzten 24 Stunden wurden keine neuen Buergercert Sicherheitswarnungen veröffentlicht.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>

        {/* Plattform Status Section */}
        <Slide direction="up" in={!loading} mountOnEnter unmountOnExit>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Plattform Status
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Hier siehst du den aktuellen Status wichtiger Online-Plattformen und Dienste. Diese Übersicht zeigt dir, welche Websites und Services gerade verfügbar sind und welche möglicherweise Probleme haben. Das hilft dir dabei zu verstehen, ob Probleme bei dir liegen oder ob ein Dienst tatsächlich nicht erreichbar ist.
                    <br/><br/>
                    Dieser Inhalt wird exklusiv von cyberlagebild.de erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {justYouPlatforms && justYouPlatforms.length > 0 ? (
                  <Box>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', md: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', md: 'center' },
                      marginBottom: 3,
                      padding: { xs: 1.5, md: 2 },
                      background: 'rgba(66, 165, 245, 0.1)',
                      borderRadius: 2,
                      border: '1px solid rgba(66, 165, 245, 0.3)',
                      gap: { xs: 2, md: 0 }
                    }}>
                      <Typography variant="h6" sx={{ 
                        color: '#42a5f5', 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: { xs: '1rem', md: '1.25rem' }
                      }}>
                        📊 Status Übersicht
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: { xs: 1, md: 2 }, 
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}>
                        <Chip 
                          label={`${justYouSummary.up_platforms || 0} Online`}
                          sx={{ 
                            backgroundColor: '#4CAF50',
                            color: '#ffffff',
                            fontWeight: 'bold',
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            height: { xs: 24, md: 32 }
                          }}
                        />
                        <Chip 
                          label={`${justYouSummary.down_platforms || 0} Offline`}
                          sx={{ 
                            backgroundColor: '#f44336',
                            color: '#ffffff',
                            fontWeight: 'bold',
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            height: { xs: 24, md: 32 }
                          }}
                        />
                        <Chip 
                          label={`${justYouSummary.uptime_percentage || 0}% Verfügbarkeit`}
                          sx={{ 
                            backgroundColor: '#2196f3',
                            color: '#ffffff',
                            fontWeight: 'bold',
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            height: { xs: 24, md: 32 }
                          }}
                        />
                      </Box>
                    </Box>
                    
                    <Grid container spacing={{ xs: 1, md: 2 }} sx={{ 
                      '& .MuiGrid-item': {
                        paddingLeft: { xs: '4px', md: '8px' }
                      }
                    }}>
                      {justYouPlatforms.map((platform, index) => (
                        <Grid item xs={12} sm={6} md={4} key={platform.platform || index}>
                          <Box sx={{
                            padding: { xs: 1.5, md: 2 },
                            background: platform.current_status === 'UP' 
                              ? 'rgba(76, 175, 80, 0.1)' 
                              : 'rgba(244, 67, 54, 0.1)',
                            border: platform.current_status === 'UP' 
                              ? '1px solid rgba(76, 175, 80, 0.3)' 
                              : '1px solid rgba(244, 67, 54, 0.3)',
                            borderRadius: 2,
                            borderLeft: `4px solid ${platform.current_status === 'UP' ? '#4CAF50' : '#f44336'}`,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            }
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              marginBottom: 1,
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'flex-start', sm: 'center' }
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                {platform.favicon ? (
                                  <Box
                                    component="img"
                                    src={platform.favicon}
                                    alt={`${platform.platform} icon`}
                                    sx={{
                                      width: { xs: 20, md: 24 },
                                      height: { xs: 20, md: 24 },
                                      borderRadius: 2,
                                      border: '1px solid rgba(255, 255, 255, 0.2)',
                                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                      padding: '2px',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <LanguageIcon 
                                    sx={{ 
                                      width: { xs: 20, md: 24 }, 
                                      height: { xs: 20, md: 24 }, 
                                      color: '#42a5f5',
                                      backgroundColor: 'rgba(66, 165, 245, 0.1)',
                                      borderRadius: 2,
                                      padding: '2px'
                                    }} 
                                  />
                                )}
                                <Typography variant="subtitle1" sx={{ 
                                  color: '#ffffff', 
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                  fontSize: { xs: '0.9rem', md: '1rem' }
                                }}>
                                  {platform.platform}
                                </Typography>
                              </Box>
                              <Chip 
                                label={platform.current_status === 'UP' ? 'Online' : 'Offline'}
                                size="small"
                                sx={{ 
                                  backgroundColor: platform.current_status === 'UP' ? '#4CAF50' : '#f44336',
                                  color: '#ffffff',
                                  fontWeight: 'bold',
                                  fontSize: { xs: '0.65rem', md: '0.7rem' },
                                  height: { xs: 20, md: 24 }
                                }}
                              />
                            </Box>
                            
                            {platform.latency_ms && (
                              <Typography variant="body2" sx={{ 
                                color: '#cccccc',
                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                marginBottom: 0.5
                              }}>
                                ⚡ Antwortzeit: {platform.latency_ms}ms
                              </Typography>
                            )}
                            
                            {platform.http_status && (
                              <Typography variant="body2" sx={{ 
                                color: '#cccccc',
                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                marginBottom: 0.5
                              }}>
                                📡 HTTP Status: {platform.http_status}
                              </Typography>
                            )}
                            
                            {platform.error && (
                              <Typography variant="body2" sx={{ 
                                color: '#ff9800',
                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                fontStyle: 'italic'
                              }}>
                                ⚠️ {platform.error}
                              </Typography>
                            )}
                            
                            <Typography variant="body2" sx={{ 
                              color: '#aaaaaa',
                              fontSize: { xs: '0.65rem', md: '0.7rem' },
                              marginTop: 1
                            }}>
                              Letzte Prüfung: {platform.last_check ? new Date(platform.last_check).toLocaleString('de-DE') : 'Unbekannt'}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    padding: 4,
                    color: '#42a5f5'
                  }}>
                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                      Keine Plattform-Daten verfügbar
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Aktuell sind keine Plattform-Status-Daten verfügbar.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>



        {/* Cyberpuls Section */}
        <Slide direction="up" in={!loading} mountOnEnter unmountOnExit>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Cyberpuls
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Der Cyberpuls zeigt dir, was Deutschland derzeit online bewegt. Diese Übersicht zeigt die meistgesuchten Begriffe und Themen im Internet. So siehst du auf einen Blick, welche Themen gerade aktuell sind und was die Menschen beschäftigt.
                    <br/><br/>
                    Dieser Inhalt wird exklusiv von cyberlagebild.de mit der Hilfe von Google Trends erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {googleTrends && googleTrends.length > 0 ? (
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: 'white', 
                      marginBottom: 2,
                      fontSize: { xs: '0.85rem', md: '0.875rem' }
                    }}>
                      Was Deutschland derzeit online bewegt - Die meistgesuchten Begriffe und Themen im Netz
                    </Typography>
                    
                    <Box sx={{ 
                      height: { xs: 300, sm: 350, md: 400 }, 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.05) 100%)',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      minHeight: { xs: 300, sm: 350, md: 400 },
                      width: '100%',
                      maxWidth: '100%'
                    }}>
                      <ReactECharts
                        option={{
                          backgroundColor: 'transparent',
                          tooltip: {
                            show: true,
                            formatter: function(params) {
                              return `${params.data.name}<br/>${params.data.value.toLocaleString()} Suchanfragen`;
                            }
                          },
                          series: [{
                            type: 'wordCloud',
                            shape: 'circle',
                            left: 'center',
                            top: 'center',
                            width: '80%',
                            height: '80%',
                            right: null,
                            bottom: null,
                            sizeRange: [14, 50],
                            rotationRange: [-45, 45],
                            rotationStep: 15,
                            gridSize: 6,
                            drawOutOfBound: false,
                            layoutAnimation: false,
                            textStyle: {
                              fontFamily: 'sans-serif',
                              fontWeight: 'bold',
                              color: function () {
                                const colors = ['#4CAF50', '#2196F3', '#FF9800', '#F44336', '#9C27B0', '#00BCD4', '#FFEB3B', '#795548'];
                                return colors[Math.floor(Math.random() * colors.length)];
                              }
                            },
                            emphasis: {
                              focus: 'self',
                              textStyle: {
                                textShadowBlur: 10,
                                textShadowColor: '#333'
                              }
                            },
                            data: googleTrends.map(topic => ({
                              name: topic.topic,
                              value: topic.search_volume,
                              searchVolume: topic.search_volume,
                              exploreUrl: topic.explore_url
                            }))
                          }]
                        }}
                        style={{ height: '100%', width: '100%' }}
                        onEvents={{
                          click: (params) => {
                            if (params.data && params.data.exploreUrl) {
                              window.open(params.data.exploreUrl, '_blank');
                            }
                          }
                        }}
                        opts={{
                          renderer: 'canvas'
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ 
                      marginTop: 2, 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: 'white', 
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                        textAlign: { xs: 'center', sm: 'left' }
                      }}>
                        💡 Klicken Sie auf ein Wort für mehr Details
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: 'white', 
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                        textAlign: { xs: 'center', sm: 'right' }
                      }}>
                        Aktuell {googleTrends.length} Trending Topics
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: 200,
                    color: 'white',
                    fontStyle: 'italic'
                  }}>
                    Keine Trending Topics verfügbar
                  </Box>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>

        {/* Buzzlator Terms Section */}
        <Slide direction="up" in={!loading} mountOnEnter unmountOnExit>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: 2 }}>
              <SectionTitle variant="h4" component="h2">
                Buzzlator Begriffe
                {buzzlatorTermsLoading && (
                  <CircularProgress 
                    size={20} 
                    sx={{ 
                      ml: 2, 
                      color: '#42a5f5',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }} 
                  />
                )}
              </SectionTitle>
              <Tooltip 
                title={
                  <div>
                    Hier siehst du alle Begriffe, die bereits im Buzzlator erklärt wurden. Die Größe der Wörter zeigt, wie oft sie nachgefragt wurden. So erkennst du, welche technischen Begriffe besonders häufig erklärt werden müssen.
                    <br/><br/>
                    <strong>🔄 Live-Updates:</strong> Diese Liste wird automatisch aktualisiert, sobald ein neues Buzzword im Buzzlator erklärt wird.
                    <br/><br/>
                    Dieser Inhalt wird exklusiv von cyberlagebild.de erstellt.
                  </div>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      maxWidth: '400px',
                      padding: '12px 16px',
                    }
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#42a5f5',
                    marginTop: '-12px',
                    '&:hover': { 
                      color: '#2196f3',
                      backgroundColor: 'rgba(66, 165, 245, 0.1)'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <DashboardCard elevation={0}>
              <Box sx={{ padding: 3 }}>
                {buzzlatorTerms && buzzlatorTerms.length > 0 ? (
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: 'white', 
                      marginBottom: 2,
                      fontSize: { xs: '0.85rem', md: '0.875rem' }
                    }}>
                      Alle Begriffe, die bereits im Buzzlator erklärt wurden - Die Größe zeigt die Nachfrage
                    </Typography>
                    
                    <Box sx={{ 
                      height: { xs: 300, sm: 350, md: 400 }, 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.05) 100%)',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      minHeight: { xs: 300, sm: 350, md: 400 },
                      width: '100%',
                      maxWidth: '100%'
                    }}>
                      <ReactECharts
                        option={{
                          backgroundColor: 'transparent',
                          tooltip: {
                            show: true,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            borderColor: '#42a5f5',
                            borderWidth: 1,
                            textStyle: {
                              color: '#fff',
                              fontSize: 12,
                              lineHeight: 16
                            },
                            extraCssText: 'max-width: 200px; word-wrap: break-word; padding: 6px; white-space: pre-wrap;',
                            formatter: function(params) {
                              const requestText = params.data.value === 1 ? 'Anfrage' : 'Anfragen';
                              let tooltip = `<strong>${params.data.name}</strong><br/>${params.data.value} ${requestText}<br/><br/>`;
                              
                              if (params.data.oneSentence) {
                                // Add line breaks to make text wrap nicely
                                const explanation = params.data.oneSentence;
                                // Split long sentences at natural break points
                                const words = explanation.split(' ');
                                let wrappedText = '';
                                let currentLine = '';
                                
                                for (let i = 0; i < words.length; i++) {
                                  const word = words[i];
                                  if ((currentLine + word).length > 25) { // Break at ~25 characters
                                    wrappedText += currentLine.trim() + '<br/>';
                                    currentLine = word + ' ';
                                  } else {
                                    currentLine += word + ' ';
                                  }
                                }
                                wrappedText += currentLine.trim();
                                
                                tooltip += wrappedText;
                              }
                              
                              return tooltip;
                            }
                          },
                          series: [{
                            type: 'wordCloud',
                            shape: 'circle',
                            left: 'center',
                            top: 'center',
                            width: '80%',
                            height: '80%',
                            right: null,
                            bottom: null,
                            sizeRange: [14, 50],
                            rotationRange: [-45, 45],
                            rotationStep: 15,
                            gridSize: 6,
                            drawOutOfBound: false,
                            layoutAnimation: false,
                            textStyle: {
                              fontFamily: 'sans-serif',
                              fontWeight: 'bold',
                              color: function () {
                                const colors = ['#4CAF50', '#2196F3', '#FF9800', '#F44336', '#9C27B0', '#00BCD4', '#FFEB3B', '#795548'];
                                return colors[Math.floor(Math.random() * colors.length)];
                              }
                            },
                            emphasis: {
                              focus: 'self',
                              textStyle: {
                                textShadowBlur: 10,
                                textShadowColor: '#333'
                              }
                            },
                            data: buzzlatorTerms.map(term => ({
                              name: term.term,
                              value: term.count,
                              count: term.count,
                              securityScore: term.security_relevance_score,
                              crisisScore: term.crisis_relevance_score,
                              locale: term.locale,
                              audience: term.audience,
                              model: term.model,
                              oneSentence: term.one_sentence,
                              simpleExplanation: term.simple_explanation,
                              analogy: term.analogy,
                              example: term.example
                            }))
                          }]
                        }}
                        style={{ height: '100%', width: '100%' }}
                        onEvents={{
                          click: (params) => {
                            if (params.data && params.data.name) {
                              // You could add functionality here to show term details or navigate to buzzlator
                              console.log('Clicked term:', params.data.name);
                            }
                          }
                        }}
                        opts={{
                          renderer: 'canvas'
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ 
                      marginTop: 2, 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: 'white', 
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                        textAlign: { xs: 'center', sm: 'left' }
                      }}>
                        💡 Klicken Sie auf ein Wort für mehr Details
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: 'white', 
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                        textAlign: { xs: 'center', sm: 'right' }
                      }}>
                        Aktuell {buzzlatorTerms.length} erklärte Begriffe
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: 200,
                    color: 'white',
                    fontStyle: 'italic'
                  }}>
                    Keine Buzzlator Begriffe verfügbar
                  </Box>
                )}
              </Box>
            </DashboardCard>
          </Box>
        </Slide>


      </StyledContainer>

      <StyledFooter>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3
        }}>
          {/* Wichtiger Hinweis */}
          <Box sx={{ 
            maxWidth: '800px',
            width: '100%',
            padding: 2,
            background: 'rgba(66, 165, 245, 0.1)',
            border: '1px solid rgba(66, 165, 245, 0.3)',
            borderRadius: 2,
            textAlign: 'center'
          }}>
            <Typography variant="h6" sx={{ 
              color: '#42a5f5', 
              fontWeight: 'bold', 
              marginBottom: 1
            }}>
              Wichtiger Hinweis
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#ffffff', 
              lineHeight: 1.6,
              fontSize: '0.9rem'
            }}>
              Die Webseite befindet sich in der Testphase. Sie ist im ständigen Aufbau und wird kontinuierlich verbessert. 
              Diese Website enthält stellenweise KI-generierte Inhalte. Die Inhalte der verlinkten Webseiten spiegeln nicht meine Meinung wieder und ich übernehme keine Verantwortung dafür.
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ fontWeight: 500, color: 'white' }}>
            © 2025 Cyberlagebild.de - Alle Rechte vorbehalten.
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 2, sm: 3 }, 
            flexWrap: 'wrap', 
            justifyContent: 'center',
            padding: { xs: 1, sm: 0 }
          }}>
            <Link 
              href="https://www.cyberlagebild.de/impressum"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                color: 'white', 
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { 
                  textDecoration: 'underline',
                  color: '#e3f2fd'
                }
              }}
            >
              Impressum
            </Link>
            <Link 
              href="https://www.cyberlagebild.de/datenschutzerklaerung"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                color: 'white', 
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { 
                  textDecoration: 'underline',
                  color: '#e3f2fd'
                }
              }}
            >
              Datenschutzerklärung
            </Link>
            <Link 
              href="https://www.cyberlagebild.de/haftungsausschluss"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                color: 'white', 
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { 
                  textDecoration: 'underline',
                  color: '#e3f2fd'
                }
              }}
            >
              Haftungsausschluss
            </Link>
            <Link 
              href="https://www.cyberlagebild.de/cookie-richtlinie"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                color: 'white', 
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { 
                  textDecoration: 'underline',
                  color: '#e3f2fd'
                }
              }}
            >
              Cookie-Richtlinie
            </Link>
          </Box>
        </Box>
      </StyledFooter>
        </Box>
      </Box>
    </Box>
  );
};

export default Bevoelkerung;
